import React, { Component } from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import * as XLSX from "xlsx";
import DateRangePicker from "react-daterange-picker";
import HelperClass from "./../../../config/helperClass";
import { AGENT_PORTAL_URL } from "../../../config/config";
import {
  getResellerInvoices,
  createResellerInvoice,
  getSingleResellerInvoice,
  getCompletedOrdersWithoutInvoice,
  getSalesOrdersForInvoice,
  getInvoiceBySalesOrder,
  addInvoice,
  updateInvoice,
  notifyInvoice,
  deleteResellerInvoice,
} from "./../../../config/reseller_api_calls";
import { addSettings, addMsaInfo } from "../../../actions";
import { getClientCreditLedger, listClientsWithCredits, getClientCreditSummary } from "./../../../config/api_calls";
import MsaWarning from "../ExtraComponents/MsaWarning";
import SettingsError from "../ExtraComponents/SettingsError";
import CreateInvoiceModal from "../../../components/Invoices/CreateInvoiceModal";
import EditInvoiceModal from "../../../components/Invoices/EditInvoiceModal";
import RecordPaymentModal from "../../../components/Invoices/RecordPaymentModal";
import ViewInvoiceModal from "../../../components/Invoices/ViewInvoiceModal";
import NotificationToast from "../../../components/Notification/NotificationToast";
import { toDateInputValue } from "../../../utils/helpers";
import "./styles.css";

const auth = new HelperClass();

// ── Helpers ───────────────────────────────────────────────────────────────────

function isOverdue(row) {
  if (!row.due_date) return false;
  if (row.payment_status === "Paid") return false;
  return new Date(row.due_date) < new Date();
}

function fmtCompact(val) {
  const n = Number(val);
  if (!Number.isFinite(n)) return "$0";
  if (n >= 1000) return "$" + (n / 1000).toFixed(1).replace(/\.0$/, "") + "K";
  return "$" + n.toFixed(0);
}

function fmtDate(val) {
  if (!val) return "—";
  try {
    const d = new Date(val);
    if (isNaN(d.getTime())) return val;
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${mm}-${dd}-${d.getFullYear()}`;
  } catch {
    return val;
  }
}

function fmtCurrency(val, decimals) {
  const n = Number(val);
  if (!Number.isFinite(n)) return "$0.00";
  const fixed = n.toFixed(decimals || 2);
  const [intPart, decPart] = fixed.split(".");
  const withCommas = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return "$" + (decPart !== undefined ? withCommas + "." + decPart : withCommas);
}

function fmtAddress(entity) {
  if (!entity) return "";
  return [
    entity.address,
    [entity.city, entity.state, entity.zip].filter(Boolean).join(", "),
    entity.country,
  ]
    .filter(Boolean)
    .join(" • ");
}

function getInvoiceBalance(invoice) {
  if (invoice && invoice.balance_due != null) return Math.max(0, Number(invoice.balance_due));
  return Math.max(
    0,
    (Number(invoice && invoice.grand_total) || 0) -
      (Number(invoice && invoice.credit_applied) || 0) -
      (Number(invoice && invoice.amount_paid) || 0)
  );
}

function todayStr() {
  // Local, not UTC — toISOString() stamps yesterday's date late in the evening
  // east of Greenwich.
  return toDateInputValue(new Date());
}

// Display-only label: stored value stays "Pending"; UI shows "Unpaid".
function paymentStatusLabel(s) {
  return !s || s === "Pending" ? "Unpaid" : s;
}

// ── Status badge ──────────────────────────────────────────────────────────────

const BADGE = {
  Paid:    { bg: "rgba(26,122,74,0.1)",   color: "#1A7A4A", label: "✓ Paid"     },
  Pending: { bg: "rgba(245,158,11,0.1)",  color: "#D97706", label: "⏳ Unpaid"  },
  Partial: { bg: "rgba(28,91,222,0.1)",   color: "#1C5BDE", label: "◑ Partial"  },
};

function StatusBadge({ status }) {
  const s = BADGE[status] || BADGE.Pending;
  return (
    <span className="inv-status-badge" style={{ background: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
}

// ── Empty line item ───────────────────────────────────────────────────────────

function emptyItem() {
  return { description: "", qty: 1, unit_price: "" };
}

// ─────────────────────────────────────────────────────────────────────────────

class Invoices extends Component {
  constructor(props) {
    super(props);
    this._decimals = Number(auth.getBillingDecimalPoint() || 2);
    this._searchTimer = null;
    this._notifyBodyRef = React.createRef();

    this.state = {
      // ── toast ──
      toastMsg: "",
      toastType: "success",

      // ── list ──
      rows: [],
      total: 0,
      summary: { total_invoiced: 0, total_paid: 0, total_outstanding: 0, invoice_count: 0 },
      creditTotals: { total_balance: 0, clients_with_credit: 0, is_client_specific: false },
      page: 1,
      limit: 10,
      search: "",
      statusFilter: "",
      orderFilter: "",
      clientFilter: "",
      dateFrom: "",
      dateTo: "",
      loading: false,
      errorMessage: "",

      // ── view: "list" | "detail" | "create" | "edit" ──
      activeView: "list",

      // ── detail (managed by ViewInvoiceModal) ──
      selectedInvoice: null,

      // ── create / edit form ──
      formLoading: false,
      formError: "",
      formSuccess: "",
      formSubmitting: false,
      // form fields
      fOrderno: "",
      fClientAccountno: "",
      fResellerAccountno: "",
      fInvoiceTitle: "",
      fCustomerName: "",
      fCustomerEmail: "",
      fInvoiceDate: todayStr(),
      fDueDate: "",
      fPaymentTerms: "Due on receipt",
      fCurrency: "USD",
      fInvoiceType: "standard",
      fNotes: "",
      fDiscountTotal: "0",
      fTaxTotal: "0",
      fShippingTotal: "0",
      fPaymentMethod: "",
      fPaymentStatus: "Pending",
      fAmountPaid: "0",
      fPaymentDate: "",
      fPaymentReference: "",
      fBillingPeriod: "",
      fLineItems: [emptyItem()],

      // create flow: pick sales order first
      showCreateFromOrderModal: false,
      salesOrdersForInvoice: [],
      salesOrdersLoading: false,
      selectedOrderForCreate: "",
      orderSearchTerm: "",
      orderSearchOpen: false,
      existingOrderInvoices: [],
      existingOrderInvoicesLoading: false,

      // ── generate-from-order modal (preserved) ──
      showGenerateModal: false,
      completedOrders: [],
      completedOrdersLoading: false,
      genOrderno: "",
      genDueDate: "",
      genPaymentTerms: "Due on receipt",
      genNotes: "",
      genSubmitting: false,
      genError: "",
      genSuccess: "",

      // ── edit modal ──
      showEditModal: false,
      editInvoice: null,

      // ── pay modal ──
      showPayModal: false,
      payInvoice: null,

      // ── action menu ──
      openMenuRow: null,

      // ── view modal ──
      showViewModal: false,

      // ── notify modal ──
      showNotifyModal: false,
      notifyFromName: "",
      notifyFromEmail: "",
      notifyToName: "",
      notifyTo: "",
      notifyCc: "",
      notifySubject: "",
      notifyContent: "",
      notifyAttachPdf: true,
      notifyMarkSent: true,
      notifyLogActivity: true,
      notifyBccSelf: false,
      notifySubmitting: false,
      notifyError: "",
      notifySuccess: "",

      // ── reusable Create Invoice modal ──
      showFollowUpModal: false,
      followUpMode: "follow_up",
      followUpSalesOrder: null,

      // ── delete (soft) modal ──
      showDeleteModal: false,
      deleteInvoice: null,
      deleteReason: "",
      deleteError: "",
      deleteSubmitting: false,

      // ── client credit ledger modal ──
      showLedgerModal: false,
      ledgerClientAccountno: "",
      ledgerClientName: "",
      ledgerRows: [],
      ledgerLoading: false,
      ledgerError: "",
    };
  }

  componentDidMount() {
    const params = new URLSearchParams(this.props.location && this.props.location.search ? this.props.location.search : "");
    const orderno = params.get("orderno");
    if (orderno) {
      this.setState({ orderFilter: orderno }, this.fetchInvoices);
    } else {
      this.fetchInvoices();
    }
  }

  fetchCreditTotals = async (clientAccountno = "") => {
    try {
      if (clientAccountno) {
        const response = await getClientCreditSummary(auth.getAccount(), auth.getToken(), clientAccountno);
        const payload = response && response.data;
        if (payload && payload.status === 200 && payload.data) {
          const s = payload.data.summary || {};
          this.setState({
            creditTotals: {
              total_balance: Number(s.balance) || 0,
              clients_with_credit: 0,
              is_client_specific: true,
            },
          });
        }
      } else {
        const response = await listClientsWithCredits(auth.getAccount(), auth.getToken(), 1, 10000, "");
        const payload = response && response.data;
        if (payload && payload.status === 200 && payload.data && payload.data.totals) {
          this.setState({ creditTotals: { ...payload.data.totals, is_client_specific: false } });
        }
      }
    } catch {}
  };

  // ── Data fetching ─────────────────────────────────────────────────────────

  fetchInvoices = async () => {
    const { page, limit, search, statusFilter, orderFilter, clientFilter, dateFrom, dateTo } = this.state;
    this.setState({ loading: true, errorMessage: "" });
    try {
      const response = await getResellerInvoices(
        auth.getAccount(), auth.getToken(),
        page, limit, search, statusFilter, dateFrom, dateTo,
        orderFilter, clientFilter
      );
      const payload = response && response.data;
      if (payload && (payload.status === 403 || payload.errors === "authentication missing" || payload.errors === "jwt expired")) {
        auth.logout(); return;
      }
      if (payload && payload.status === 200 && payload.data) {
        const newRows = payload.data.rows || [];
        this.setState({
          rows: newRows,
          total: Number(payload.data.total) || 0,
          summary: payload.data.summary || this.state.summary,
          loading: false,
        });
        const clientAccountno = orderFilter && newRows.length > 0 ? (newRows[0].client_accountno || "") : "";
        this.fetchCreditTotals(clientAccountno);
      } else {
        const msg = (payload && payload.message) || "Failed to load invoices.";
        this.setState({ loading: false, errorMessage: msg, toastMsg: msg, toastType: "error" });
      }
    } catch {
      this.setState({ loading: false, errorMessage: "Failed to load invoices.", toastMsg: "Failed to load invoices.", toastType: "error" });
    }
  };

  loadSalesOrdersForInvoice = async (preselectedOrderno = "") => {
    this.setState({
      salesOrdersLoading: true,
      showCreateFromOrderModal: true,
      selectedOrderForCreate: preselectedOrderno || "",
      existingOrderInvoices: [],
      existingOrderInvoicesLoading: false,
      formError: "",
      formSuccess: "",
    });
    try {
      const response = await getSalesOrdersForInvoice(auth.getAccount(), auth.getToken());
      const payload = response && response.data;
      const rows = (payload && payload.status === 200 && payload.data && payload.data.rows) || [];
      this.setState({ salesOrdersForInvoice: rows, salesOrdersLoading: false });
      const orderToLoad = preselectedOrderno || (rows[0] && rows[0].orderno) || "";
      if (orderToLoad) {
        this.onSelectOrderForCreate(orderToLoad);
      }
    } catch {
      this.setState({ salesOrdersForInvoice: [], salesOrdersLoading: false });
    }
  };

  onSelectOrderForCreate = async (orderno) => {
    this.setState({
      selectedOrderForCreate: orderno,
      existingOrderInvoicesLoading: true,
      existingOrderInvoices: [],
    });
    try {
      const response = await getInvoiceBySalesOrder(auth.getAccount(), auth.getToken(), orderno);
      const payload = response && response.data;
      const rows = (payload && payload.status === 200 && payload.data && payload.data.rows) || [];
      this.setState({ existingOrderInvoices: rows, existingOrderInvoicesLoading: false });
    } catch {
      this.setState({ existingOrderInvoices: [], existingOrderInvoicesLoading: false });
    }
  };

  startCreateFromSelectedOrder = () => {
    const { selectedOrderForCreate, salesOrdersForInvoice, existingOrderInvoices } = this.state;
    if (!selectedOrderForCreate) return;
    const order = salesOrdersForInvoice.find((o) => o.orderno === selectedOrderForCreate);
    const orderTotal = Number((order && order.quote_total) || 0);
    const alreadyInvoiced =
      Number((order && order.already_invoiced) || 0) ||
      existingOrderInvoices.reduce((sum, inv) => sum + Number(inv.grand_total || 0), 0);

    this.setState({
      showCreateFromOrderModal: false,
      showFollowUpModal: true,
      followUpMode: "standard",
      followUpSalesOrder: {
        orderno: selectedOrderForCreate,
        client_accountno: (order && order.client_accountno) || "",
        client_name: (order && (order.customer_name || order.contact_person || order.client_accountno)) || "",
        client_email: (order && (order.customer_email || order.contact_email)) || "",
        customer_name: (order && (order.customer_name || order.contact_person)) || "",
        customer_email: (order && (order.customer_email || order.contact_email)) || "",
        reseller_accountno: (order && order.reseller_accountno) || auth.getAccount() || "",
        invoice_title: (order && order.quote_title) || "",
        quote_title: (order && order.quote_title) || "",
        currency: ((order && order.currency) || "USD").toUpperCase(),
        order_total: orderTotal,
        already_invoiced: alreadyInvoiced,
        billing_period: (order && order.billing_period) || "",
        payment_terms: (order && order.payment_terms) || "",
      },
      openMenuRow: null,
    });
  };

  openDetail = (row) => {
    this.setState({ showViewModal: true, selectedInvoice: row, openMenuRow: null });
  };

  closeViewModal = () => {
    this.setState({ showViewModal: false, selectedInvoice: null });
  };

  openCreate = async (preselectedOrderno = "") => {
    this.loadSalesOrdersForInvoice(preselectedOrderno);
  };

  openEdit = async (row) => {
    this.setState({ activeView: "edit", formError: "", formSuccess: "", openMenuRow: null });
    // Fetch full invoice data
    try {
      const response = await getSingleResellerInvoice(auth.getAccount(), auth.getToken(), row.invoice_no);
      const payload = response && response.data;
      if (payload && payload.status === 200 && payload.data) {
        const inv = payload.data.invoice || row;
        const services = payload.data.services || [];
        const lineItems = services.length > 0
          ? services.map(s => ({ description: s.description || s.title || "", qty: Number(s.quantity) || 1, unit_price: s.unit_price || s.price || "" }))
          : [{ description: inv.invoice_title || "", qty: 1, unit_price: inv.sub_total || "" }];
        this.setState({
          selectedInvoice: inv,
          fOrderno: inv.orderno || "",
          fClientAccountno: inv.client_accountno || "",
          fInvoiceTitle: inv.invoice_title || "",
          fCustomerName: inv.customer_name || "",
          fCustomerEmail: inv.customer_email || "",
          fInvoiceDate: (inv.invoice_date || "").slice(0, 10),
          fDueDate: (inv.due_date || "").slice(0, 10),
          fPaymentTerms: inv.payment_terms || "Due on receipt",
          fCurrency: inv.currency || "USD",
          fInvoiceType: inv.invoice_type || "standard",
          fNotes: inv.notes || "",
          fDiscountTotal: String(Number(inv.discount_total) || 0),
          fTaxTotal: String(Number(inv.tax_total) || 0),
          fShippingTotal: String(Number(inv.shipping_total) || 0),
          fPaymentMethod: inv.payment_method || "",
          fPaymentStatus: inv.payment_status || "Pending",
          fAmountPaid: String(Number(inv.amount_paid) || 0),
          fPaymentDate: inv.payment_date ? String(inv.payment_date).slice(0, 10) : "",
          fPaymentReference: inv.payment_reference || "",
          fLineItems: lineItems,
        });
      }
    } catch {}
  };

  backToList = () => {
    this.setState({ activeView: "list", selectedInvoice: null, showViewModal: false });
    this.fetchInvoices();
  };

  openFollowUpCreate = (invoiceData) => {
    const inv = invoiceData || this.state.selectedInvoice;
    if (!inv) return;
    const grandTotal = Number(inv.grand_total) || 0;
    const amountPaid = Number(inv.amount_paid) || 0;
    const creditApplied = Number(inv.credit_applied) || 0;

    this.setState({
      showFollowUpModal: true,
      followUpMode: "follow_up",
      followUpSalesOrder: {
        orderno: inv.orderno || "",
        client_accountno: inv.client_accountno || "",
        client_name: inv.customer_name || "",
        client_email: inv.customer_email || "",
        customer_name: inv.customer_name || "",
        customer_email: inv.customer_email || "",
        reseller_accountno: inv.reseller_accountno || auth.getAccount() || "",
        invoice_title: (inv.invoice_title || "") + " (Follow-up)",
        currency: (inv.currency || "USD").toUpperCase(),
        order_total: grandTotal,
        already_invoiced: amountPaid + creditApplied,
        billing_period: inv.billing_period || "",
        payment_terms: inv.payment_terms || "",
      },
    });

  };

  close_follow_up_modal = () => {
    this.setState({ showFollowUpModal: false, followUpSalesOrder: null });
  };

  handleFollowUpSuccess = () => {
    this.setState({ showFollowUpModal: false, followUpSalesOrder: null, showViewModal: false });
    this.fetchInvoices();
  };

  openNotifyModal = (invoiceData) => {
    const inv = invoiceData || this.state.selectedInvoice;
    if (!inv) return;
    const detailReseller = inv.reseller || null;
    const detailClient   = inv.client   || null;

    const invoiceNo   = inv.invoice_no || "";
    const grandTotal  = Number(inv.grand_total) || 0;
    const balanceDue  = getInvoiceBalance(inv);
    const clientName  = (detailClient && (detailClient.comp_name || detailClient.name)) || inv.customer_name || "Client";
    const resellerName = (detailReseller && detailReseller.comp_name) || "";
    const resellerEmail = (detailReseller && (detailReseller.billing_email || detailReseller.email)) || "";
    const period = inv.billing_period ? `\n\nBilling Period: ${inv.billing_period}` : "";

    const defaultSubject = `Invoice ${invoiceNo} — Payment Required — ${clientName}`;
    const defaultContent =
`Dear ${clientName},

Please find your invoice for the billing period ${inv.billing_period || "—"}.

Total Amount Due: $${grandTotal.toFixed(2)}
Balance Due: $${balanceDue.toFixed(2)}
Due Date: ${fmtDate(inv.due_date)}
Payment Terms: ${inv.payment_terms || "Net 30"}

If you have any questions about this invoice, please don't hesitate to contact us.

Best regards,
${resellerName ? resellerName + " Billing Team" : "Billing Team"}${resellerEmail ? "\n" + resellerEmail : ""}`;

    this.setState({
      showNotifyModal: true,
      notifyFromName:  resellerName ? resellerName + " Billing Team" : "Billing Team",
      notifyFromEmail: resellerEmail,
      notifyToName:    clientName,
      notifyTo:        inv.customer_email || (detailClient && detailClient.email) || "",
      notifyCc:        "",
      notifySubject:   defaultSubject,
      notifyContent:   defaultContent,
      notifyAttachPdf: true,
      notifyMarkSent:  true,
      notifyLogActivity: true,
      notifyBccSelf:   false,
      notifySubmitting: false,
      notifyError:     "",
      notifySuccess:   "",
    });
  };

  closeNotifyModal = () => {
    if (this.state.notifySubmitting) return;
    this.setState({ showNotifyModal: false });
  };

  submitNotify = async () => {
    const { selectedInvoice, notifyTo, notifyCc, notifySubject, notifyContent, notifyBccSelf, notifyFromEmail } = this.state;
    const inv = selectedInvoice;
    if (!inv) return;
    if (!notifyTo) { this.setState({ notifyError: "To email address is required." }); return; }
    if (!notifySubject) { this.setState({ notifyError: "Subject is required." }); return; }
    if (!notifyContent) { this.setState({ notifyError: "Message content is required." }); return; }
    this.setState({ notifySubmitting: true, notifyError: "", notifySuccess: "" });
    const bcc = notifyBccSelf && notifyFromEmail ? notifyFromEmail : "";
    try {
      const response = await notifyInvoice(
        auth.getAccount(), auth.getToken(),
        inv.invoice_no,
        notifyTo, notifyCc, bcc, notifySubject, notifyContent
      );
      const payload = response && response.data;
      if (payload && payload.status === 200) {
        this.setState({ notifySubmitting: false, notifySuccess: "Invoice email sent successfully!", toastMsg: "Invoice email sent successfully!", toastType: "success" });
        setTimeout(() => this.setState({ showNotifyModal: false, showViewModal: false }), 3500);
      } else {
        const msg = (payload && payload.message) || "Failed to send email.";
        this.setState({ notifySubmitting: false, notifyError: msg, toastMsg: msg, toastType: "error" });
      }
    } catch {
      this.setState({ notifySubmitting: false, notifyError: "Failed to send email.", toastMsg: "Failed to send email.", toastType: "error" });
    }
  };

  // ── Pagination / filters ──────────────────────────────────────────────────

  onPageChange    = (page)  => this.setState({ page },               this.fetchInvoices);
  onLimitChange   = (limit) => this.setState({ limit, page: 1 },     this.fetchInvoices);
  onSearchChange  = (e) => {
    const search = e.target.value;
    this.setState({ search, page: 1 });
    clearTimeout(this._searchTimer);
    this._searchTimer = setTimeout(this.fetchInvoices, 400);
  };
  onStatusFilter  = (e) => this.setState({ statusFilter: e.target.value,  page: 1 }, this.fetchInvoices);
  onOrderFilter   = (e) => this.setState({ orderFilter: e.target.value,   page: 1 }, this.fetchInvoices);
  onClientFilter  = (e) => this.setState({ clientFilter: e.target.value,  page: 1 }, this.fetchInvoices);
  onDateFrom      = (e) => this.setState({ dateFrom: e.target.value,      page: 1 }, this.fetchInvoices);
  onDateTo        = (e) => this.setState({ dateTo:   e.target.value,      page: 1 }, this.fetchInvoices);
  clearOrderFilter = () => {
    const params = new URLSearchParams(this.props.location && this.props.location.search ? this.props.location.search : "");
    if (params.has("orderno")) {
      params.delete("orderno");
      const newSearch = params.toString();
      this.props.history.replace({ search: newSearch ? `?${newSearch}` : "" });
    }
    this.setState({ orderFilter: "" }, this.fetchInvoices);
  };

  exportToExcel = () => {
    const { rows } = this.state;
    if (!rows || rows.length === 0) return;

    const data = rows.map((r) => ({
      "Invoice #":       r.invoice_no || "",
      "Order #":         r.orderno || "",
      "Client":          r.customer_name || "",
      "Client Email":    r.customer_email || "",
      "Invoice Type":    r.invoice_type === "partial" ? "Partial Invoice" : r.invoice_type === "follow_up" ? "Follow-up Invoice" : "Standard Invoice",
      "Issue Date":      r.invoice_date ? r.invoice_date.slice(0, 10) : "",
      "Due Date":        r.due_date     ? r.due_date.slice(0, 10)     : "",
      "Billing Period":  r.billing_period || "",
      "Currency":        r.currency || "USD",
      "Sub Total":       Number(r.sub_total    || 0),
      "Discount":        Number(r.discount_total || 0),
      "Tax":             Number(r.tax_total    || 0),
      "Shipping":        Number(r.shipping_total || 0),
      "Grand Total":     Number(r.grand_total  || 0),
      "Amount Paid":     Number(r.amount_paid  || 0),
      "Balance Due":     Number(r.balance_due  || 0),
      "Payment Status":  r.payment_status || "",
      "Payment Terms":   r.payment_terms  || "",
    }));

    const ws = XLSX.utils.json_to_sheet(data);

    // Column widths
    ws["!cols"] = [
      { wch: 18 }, { wch: 14 }, { wch: 26 }, { wch: 30 },
      { wch: 18 }, { wch: 12 }, { wch: 12 }, { wch: 24 },
      { wch: 8  }, { wch: 11 }, { wch: 10 }, { wch: 10 },
      { wch: 10 }, { wch: 12 }, { wch: 12 }, { wch: 12 },
      { wch: 14 }, { wch: 14 },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Invoices");

    const fileName = `Invoices_${new Date().toISOString().slice(0, 10)}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  downloadInvoicePdf = async (invoiceRecord) => {
    const pdfPath = invoiceRecord && invoiceRecord.pdf_path;
    if (!pdfPath) {
      this.printInvoice();
      return;
    }
    const url = AGENT_PORTAL_URL + "files_data/rfq/" + pdfPath;
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      document.body.appendChild(link);
      link.style = "display: none";
      link.href = downloadUrl;
      link.download = pdfPath;
      link.click();
      window.URL.revokeObjectURL(downloadUrl);
      link.remove();
    } catch {
      this.printInvoice();
    }
  };

  printInvoice = () => {
    const area = document.getElementById("invoice-print-area");
    if (!area) return;
    const w = window.open("", "_blank");
    w.document.write(`
      <html><head><title>Invoice</title>
      <style>
        body { font-family: Roboto, "Helvetica Neue", Arial, sans-serif; margin: 32px; color: #111; }
        table { width: 100%; border-collapse: collapse; }
        th, td { padding: 10px; border: 1px solid #ddd; font-size: 12px; }
        .navy { background: #0F3A70; color: white; }
      </style></head><body>`);
    console.log("invoice-print-area HTML:", area.innerHTML);
    w.document.write(area.innerHTML);
    w.document.write("</body></html>");
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); }, 400);
  };

  // ── Line items ────────────────────────────────────────────────────────────

  updateLineItem = (idx, field, value) => {
    const fLineItems = this.state.fLineItems.map((item, i) =>
      i === idx ? { ...item, [field]: value } : item
    );
    this.setState({ fLineItems });
  };

  addLineItem    = () => this.setState(s => ({ fLineItems: [...s.fLineItems, emptyItem()] }));
  removeLineItem = (idx) => this.setState(s => ({
    fLineItems: s.fLineItems.length > 1 ? s.fLineItems.filter((_, i) => i !== idx) : s.fLineItems,
  }));

  calcTotals() {
    const { fLineItems, fDiscountTotal, fTaxTotal, fShippingTotal } = this.state;
    const sub  = fLineItems.reduce((acc, item) => acc + (Number(item.qty) || 0) * (Number(item.unit_price) || 0), 0);
    const disc = Number(fDiscountTotal) || 0;
    const tax  = Number(fTaxTotal) || 0;
    const ship = Number(fShippingTotal) || 0;
    const grand = sub - disc + tax + ship;
    return { sub, disc, tax, ship, grand };
  }

  // ── Submit create / edit ──────────────────────────────────────────────────

  submitCreate = async () => {
    const {
      fOrderno, fClientAccountno, fResellerAccountno, fInvoiceTitle, fCustomerName, fCustomerEmail,
      fInvoiceDate, fDueDate, fPaymentTerms, fCurrency, fInvoiceType, fNotes,
      fPaymentMethod, fPaymentStatus, fAmountPaid, fPaymentDate, fPaymentReference,
      fBillingPeriod,
    } = this.state;
    if (!fOrderno)         { this.setState({ formError: "Please select a sales order." }); return; }
    if (!fInvoiceDate)      { this.setState({ formError: "Invoice date is required." }); return; }
    if (!fDueDate)          { this.setState({ formError: "Due date is required." }); return; }

    const { sub, disc, tax, ship, grand } = this.calcTotals();
    this.setState({ formSubmitting: true, formError: "", formSuccess: "" });
    try {
      const response = await addInvoice(auth.getAccount(), auth.getToken(), {
        orderno:          fOrderno,
        reseller_accountno: fResellerAccountno || auth.getAccount(),
        client_accountno: fClientAccountno,
        invoice_title:    fInvoiceTitle,
        customer_name:    fCustomerName,
        customer_email:   fCustomerEmail,
        due_date:         fDueDate,
        payment_terms:    fPaymentTerms,
        currency:         fCurrency,
        notes:            fNotes,
        billing_period:   fBillingPeriod,
        sub_total:        sub,
        discount_total:   disc,
        tax_total:        tax,
        shipping_total:   ship,
        grand_total:      grand,
        payment_method:   fPaymentMethod,
        payment_status:   fPaymentStatus,
        amount_paid:      fAmountPaid,
        payment_date:     fPaymentDate,
        payment_reference:fPaymentReference,
        invoice_type:     fInvoiceType || "standard",
      });
      const payload = response && response.data;
      if (payload && (payload.status === 403 || payload.errors === "authentication missing")) { auth.logout(); return; }
      if (payload && payload.status === 200) {
        this.setState({ formSubmitting: false, formSuccess: "Invoice created successfully!", toastMsg: "Invoice created successfully!", toastType: "success" });
        setTimeout(() => this.backToList(), 1200);
      } else {
        const msg = (payload && payload.message) || "Failed to create invoice.";
        this.setState({ formSubmitting: false, formError: msg, toastMsg: msg, toastType: "error" });
      }
    } catch {
      this.setState({ formSubmitting: false, formError: "Failed to create invoice.", toastMsg: "Failed to create invoice.", toastType: "error" });
    }
  };

  submitEdit = async () => {
    const {
      selectedInvoice, fInvoiceTitle, fCustomerName, fCustomerEmail,
      fDueDate, fPaymentTerms, fCurrency, fInvoiceType, fNotes,
      fPaymentMethod, fPaymentStatus, fAmountPaid, fPaymentDate, fPaymentReference,
    } = this.state;
    if (!selectedInvoice) return;
    const { sub, disc, tax, ship, grand } = this.calcTotals();
    this.setState({ formSubmitting: true, formError: "", formSuccess: "" });
    try {
      const response = await updateInvoice(auth.getAccount(), auth.getToken(), selectedInvoice.invoice_no, {
        invoice_title:  fInvoiceTitle,
        customer_name:  fCustomerName,
        customer_email: fCustomerEmail,
        due_date:       fDueDate,
        payment_terms:  fPaymentTerms,
        currency:       fCurrency,
        notes:          fNotes,
        sub_total:      sub,
        discount_total: disc,
        tax_total:      tax,
        shipping_total: ship,
        grand_total:    grand,
        payment_method: fPaymentMethod,
        payment_status: fPaymentStatus,
        amount_paid:    fAmountPaid,
        payment_date:   fPaymentDate,
        payment_reference: fPaymentReference,
      });
      const payload = response && response.data;
      if (payload && (payload.status === 403 || payload.errors === "authentication missing")) { auth.logout(); return; }
      if (payload && payload.status === 200) {
        this.setState({ formSubmitting: false, formSuccess: "Invoice updated successfully!", toastMsg: "Invoice updated successfully!", toastType: "success" });
        setTimeout(() => this.backToList(), 1200);
      } else {
        const msg = (payload && payload.message) || "Failed to update invoice.";
        this.setState({ formSubmitting: false, formError: msg, toastMsg: msg, toastType: "error" });
      }
    } catch {
      this.setState({ formSubmitting: false, formError: "Failed to update invoice.", toastMsg: "Failed to update invoice.", toastType: "error" });
    }
  };

  // ── Pay modal ─────────────────────────────────────────────────────────────

  openEditModal = (row) => {
    this.setState({ showEditModal: true, editInvoice: row, openMenuRow: null });
  };

  closeEditModal = () => {
    this.setState({ showEditModal: false, editInvoice: null });
  };

  handleEditSuccess = () => {
    this.setState({ showEditModal: false, editInvoice: null });
    this.fetchInvoices();
  };

  openPayModal = (row) => {
    this.setState({ showPayModal: true, payInvoice: row, openMenuRow: null });
  };

  closePayModal = () => {
    this.setState({ showPayModal: false, payInvoice: null });
  };

  handlePaymentSuccess = () => {
    this.setState({ showPayModal: false, payInvoice: null });
    this.fetchInvoices();
  };

  // ── Delete (soft) ─────────────────────────────────────────────────────────

  // Only an invoice with no money against it can go. A recorded payment writes
  // a client_sales_orders_payments_logs row carrying no invoice_no, so deleting
  // a paid invoice could never be reversed off the order's paid/remaining
  // figures. The backend enforces the same rule — this only greys the button.
  isInvoiceDeletable = (row) =>
    !!row &&
    row.payment_status === "Pending" &&
    (Number(row.amount_paid) || 0) === 0 &&
    (Number(row.credit_applied) || 0) === 0;

  openDeleteModal = (row) => {
    this.setState({
      showDeleteModal: true,
      deleteInvoice: row,
      deleteReason: "",
      deleteError: "",
      deleteSubmitting: false,
      openMenuRow: null,
    });
  };

  closeDeleteModal = () => {
    this.setState({
      showDeleteModal: false,
      deleteInvoice: null,
      deleteReason: "",
      deleteError: "",
      deleteSubmitting: false,
    });
  };

  deleteInvoiceSubmit = async () => {
    const { deleteInvoice, deleteReason } = this.state;
    const reason = (deleteReason || "").trim();

    // Whitespace-only is not a reason. The backend rejects it too — this is
    // just the faster of the two answers.
    if (reason === "") {
      this.setState({ deleteError: "Please enter a reason for deleting this invoice." });
      return;
    }
    if (!deleteInvoice) return;

    this.setState({ deleteError: "", deleteSubmitting: true });

    const response = await deleteResellerInvoice(
      auth.getAccount(),
      auth.getToken(),
      deleteInvoice.invoice_no,
      reason
    );
    const payload = response && response.data;

    if (
      payload &&
      (payload.status === 403 ||
        payload.errors === "authentication missing" ||
        payload.errors === "jwt expired")
    ) {
      auth.logout();
      return;
    }

    if (payload && payload.status === 200 && payload.message === "success") {
      // The modal closes on success, so an inline message would never be read —
      // the confirmation goes out as a toast instead.
      const invoice_no = deleteInvoice.invoice_no;
      this.closeDeleteModal();
      this.setState({ toastMsg: "" }, () =>
        this.setState({
          toastMsg: "Invoice " + invoice_no + " deleted.",
          toastType: "success",
        })
      );
      this.fetchInvoices();
    } else if (payload && payload.message === "not_deletable") {
      this.setState({
        deleteError:
          "This invoice has a recorded payment or applied credit and can no longer be deleted.",
        deleteSubmitting: false,
      });
    } else if (payload && payload.message === "not_found") {
      this.setState({
        deleteError: "The invoice was not found. Please refresh the list.",
        deleteSubmitting: false,
      });
    } else {
      this.setState({
        deleteError: "There is some error while deleting the invoice.",
        deleteSubmitting: false,
      });
    }
  };

  // ── Generate from order modal ─────────────────────────────────────────────

  openGenerateModal = async () => {
    this.openCreate();
  };

  closeGenerateModal = () => {
    this.setState({
      showCreateFromOrderModal: false,
      salesOrdersForInvoice: [],
      selectedOrderForCreate: "",
      orderSearchTerm: "",
      orderSearchOpen: false,
      existingOrderInvoices: [],
      existingOrderInvoicesLoading: false,
    });
  };

  submitGenerateInvoice = async () => {
    this.startCreateFromSelectedOrder();
  };

  // ── Render: KPI cards ─────────────────────────────────────────────────────

  renderKpiCards() {
    const { summary, rows, creditTotals } = this.state;
    const overdueCount = rows.filter(isOverdue).length;
    const unpaidCount  = rows.filter(r => r.payment_status !== "Paid").length;
    const paidCount    = rows.filter(r => r.payment_status === "Paid").length;

    // Compute from rows so partial payments are included (backend summary only counts fully-paid invoices)
    const totalCollected   = rows.reduce((sum, r) => sum + (Number(r.amount_paid) || 0), 0);
    const totalOutstanding = rows.reduce((sum, r) => sum + getInvoiceBalance(r), 0);

    const cards = [
      { label: "TOTAL INVOICES",  value: String(Number(summary.invoice_count) || 0), sub: "All time",
        iconBg: "#ede9fb", iconColor: "#7c3aed", icon: "ni ni-file-docs" },
      { label: "TOTAL OUTSTANDING",     value: fmtCompact(totalOutstanding),
        sub: `${unpaidCount} unpaid`, subExtra: overdueCount > 0 ? `· ${overdueCount} overdue` : null,
        iconBg: "#fff0e6", iconColor: "#ea580c", icon: "ni ni-clock" },
      { label: "TOTAL COLLECTED",       value: fmtCompact(totalCollected),
        sub: `${paidCount} paid invoice${paidCount !== 1 ? "s" : ""}`,
        iconBg: "#e6f9f0", iconColor: "#16a34a", icon: "ni ni-check-circle" },
      { label: "TOTAL CLIENT CREDITS",  value: fmtCompact(creditTotals.total_balance),
        sub: creditTotals.is_client_specific
          ? "client credit balance"
          : `${Number(creditTotals.clients_with_credit) || 0} client${Number(creditTotals.clients_with_credit) === 1 ? "" : "s"} with credit`,
        iconBg: "#e8eefb", iconColor: "#1c5bde", icon: "ni ni-cc-alt" },
    ];

    return (
      <div className="inv-kpi-grid">
        {cards.map((c, i) => (
          <div className="inv-kpi-card" key={i}>
            <div className="inv-kpi-left">
              <div className="inv-kpi-label">{c.label}</div>
              <div className="inv-kpi-value">{c.value}</div>
              <div className="inv-kpi-sub">
                {c.sub}
                {c.subExtra && <span className="inv-kpi-overdue-chip">{c.subExtra}</span>}
              </div>
            </div>
            <div className="inv-kpi-icon" style={{ background: c.iconBg, color: c.iconColor }}>
              <em className={`icon ${c.icon}`}></em>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // ── Client credit ledger modal ────────────────────────────────────────────

  openLedgerModal = (clientAccountno, clientName) => {
    this.setState(
      {
        showLedgerModal: true,
        ledgerClientAccountno: clientAccountno,
        ledgerClientName: clientName || "",
        ledgerRows: [],
        ledgerLoading: true,
        ledgerError: "",
      },
      this.loadLedger,
    );
  };

  closeLedgerModal = () => {
    this.setState({ showLedgerModal: false });
  };

  loadLedger = async () => {
    const { ledgerClientAccountno } = this.state;
    if (!ledgerClientAccountno) return;
    this.setState({ ledgerLoading: true, ledgerError: "" });
    try {
      const response = await getClientCreditLedger(
        auth.getAccount(), auth.getToken(),
        ledgerClientAccountno,
        1, 1000,
      );
      const payload = response && response.data;
      if (payload && (payload.status === 403 || payload.errors === "authentication missing" || payload.errors === "jwt expired")) {
        auth.logout();
        return;
      }
      if (payload && payload.status === 200 && payload.data) {
        this.setState({ ledgerRows: payload.data.rows || [], ledgerLoading: false });
      } else {
        const msg = (payload && payload.message) || "Failed to load ledger.";
        this.setState({ ledgerLoading: false, ledgerError: msg, toastMsg: msg, toastType: "error" });
      }
    } catch {
      this.setState({ ledgerLoading: false, ledgerError: "Failed to load ledger.", toastMsg: "Failed to load ledger.", toastType: "error" });
    }
  };

  formatLedgerDate = (value) => {
    if (!value) return "—";
    try {
      const d = new Date(value);
      if (isNaN(d.getTime())) return value;
      const mm = String(d.getMonth() + 1).padStart(2, "0");
      const dd = String(d.getDate()).padStart(2, "0");
      return `${mm}-${dd}-${d.getFullYear()}`;
    } catch {
      return value;
    }
  };

  // ── Render: page header ───────────────────────────────────────────────────

  renderPageHeader() {
    const params = new URLSearchParams(
      this.props.location && this.props.location.search ? this.props.location.search : ""
    );
    const orderno = params.get("orderno");

    const handleClientCredit = () => {
      if (orderno) {
        const firstRow = this.state.rows && this.state.rows[0];
        const clientAccountno = firstRow && firstRow.client_accountno;
        const clientName = (firstRow && firstRow.customer_name) || "";
        if (clientAccountno) {
          this.openLedgerModal(clientAccountno, clientName);
        } else {
          this.props.history && this.props.history.push("/client-credits");
        }
      } else {
        this.props.history && this.props.history.push("/client-credits");
      }
    };

    return (
      <div className="nk-block-head nk-block-head-sm">
        <div className="nk-block-between flex-wrap g-3">
          <div className="nk-block-head-content">
            <h3 className="nk-block-title page-title">Invoices</h3>
            <div className="nk-block-des text-soft"><p>Payments are applied per-invoice — per GAAP best practice</p></div>
          </div>
          <div className="nk-block-head-content">
            <div className="toggle-wrap nk-block-tools-toggle">
              <ul className="nk-block-tools g-3">
                <li className="nk-block-tools-opt">
                  <button
                    type="button"
                    className="btn btn-info"
                    style={{ color: "#fff" }}
                    onClick={this.exportToExcel}
                    disabled={!this.state.rows.length}
                  >
                    <em className="icon ni ni-download"></em>&nbsp;Export
                  </button>
                </li>
                <li className="nk-block-tools-opt">
                  <button type="button" className="btn btn-secondary" onClick={handleClientCredit}>
                    {orderno ? "Client Credit" : "Client Credits"}
                  </button>
                </li>
                <li className="nk-block-tools-opt">
                  {orderno ? (
                    <button type="button" className="btn btn-secondary" onClick={() => this.props.history && this.props.history.push(`/ViewSalesOrder/${orderno}`)}>
                      View Sales Order
                    </button>
                  ) : (
                    <button type="button" className="btn btn-secondary" onClick={() => this.props.history && this.props.history.push("/SalesOrders")}>
                      Sale Orders
                    </button>
                  )}
                </li>
                <li className="nk-block-tools-opt">
                  <button type="button" className="btn btn-primary" onClick={this.openCreate}>
                    + Create Invoice
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Render: filter bar ────────────────────────────────────────────────────

  renderFilters() {
    const { search, statusFilter, orderFilter, clientFilter, rows } = this.state;

    const clientOptions = Array.from(new Set(rows.map((r) => r.customer_name).filter(Boolean))).sort();

    return (
      <>
        {orderFilter && (
          <div className="inv-order-filter-banner">
            <em className="icon ni ni-filter-alt" style={{ marginRight: 6 }}></em>
            Filtering by Order <strong>{orderFilter}</strong>.{" "}
            <span className="inv-order-filter-clear" onClick={this.clearOrderFilter}>
              Show all →
            </span>
          </div>
        )}
        <div className="row g-2 align-items-center">
          <div className="col-12 col-sm col-md">
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Search invoices, clients…"
              value={search}
              onChange={this.onSearchChange}
            />
          </div>
          <div className="col-12 col-sm-auto" style={{ minWidth: 150 }}>
            <select className="form-control form-control-sm" value={statusFilter} onChange={this.onStatusFilter}>
              <option value="">All Status</option>
              <option value="Paid">Paid</option>
              <option value="Pending">Unpaid</option>
              <option value="Partial">Partial</option>
            </select>
          </div>
          <div className="col-12 col-sm-auto" style={{ minWidth: 170 }}>
            <select className="form-control form-control-sm" value={clientFilter} onChange={this.onClientFilter}>
              <option value="">All Clients</option>
              {clientOptions.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
        </div>
      </>
    );
  }

  // ── Render: pagination ───────────────────────────────────────────────────

  renderPagination() {
    const { page, limit, total, loading } = this.state;
    if (total <= 0) return null;
    const totalPages = Math.max(1, Math.ceil(total / limit));
    const start = (page - 1) * limit + 1;
    const end = Math.min(page * limit, total);

    const buildPages = () => {
      const from = Math.max(1, Math.min(page - 2, totalPages - 4));
      const to = Math.min(totalPages, from + 4);
      const pages = [];
      for (let p = from; p <= to; p++) pages.push(p);
      return pages;
    };

    return (
      <div className="card-inner" style={{ background: "#f0f2f4" }}>
        <div className="nk-block-between-md g-3">
          <div className="g">
            <span style={{ color: "#526484", fontSize: 13 }}>
              Showing {start}–{end} of {total}
            </span>
          </div>
          <div className="g">
            <ul className="pagination justify-content-center justify-content-md-end">
              <li className={"page-item " + (page <= 1 || loading ? "disabled" : "")}>
                <button type="button" className="page-link" onClick={() => this.onPageChange(page - 1)} disabled={page === 1 || loading}>
                  Prev
                </button>
              </li>
              {buildPages().map((p) => (
                <li key={p} className={"page-item " + (p === page ? "active" : "")}>
                  <button type="button" className="page-link" onClick={() => this.onPageChange(p)} disabled={loading}>
                    {p}
                  </button>
                </li>
              ))}
              <li className={"page-item " + (page >= totalPages || loading ? "disabled" : "")}>
                <button type="button" className="page-link" onClick={() => this.onPageChange(page + 1)} disabled={page === totalPages || loading}>
                  Next
                </button>
              </li>
            </ul>
          </div>
        </div>
      </div>
    );
  }

  // ── Render: listing table ─────────────────────────────────────────────────

  renderList() {
    const { rows, page, limit, total, loading, errorMessage } = this.state;
    const d = this._decimals;

    const columns = [
      {
        name: "invoice_no", label: "Invoice #",
        render: (row) => (
          <span className="inv-table-link">
            {row.invoice_no}
          </span>
        ),
      },
      {
        name: "orderno", label: "Order #",
        render: (row) => <span className="inv-table-mono">{row.orderno || "—"}</span>,
      },
      {
        name: "client", label: "Client",
        render: (row) => (
          <div style={{ minWidth: 160 }}>
            <div className="inv-table-client-name">{row.customer_name || "—"}</div>
            {row.customer_email && (
              <div className="inv-table-client-email">{row.customer_email}</div>
            )}
          </div>
        ),
      },
      {
        name: "billing_period", label: "Billing Period",
        render: (row) => <span className="inv-table-muted" style={{ whiteSpace: "nowrap" }}>{row.billing_period || "—"}</span>,
      },
      {
        name: "amount", label: "Total Amount",
        render: (row) => <span className="inv-table-amount">{fmtCurrency(row.grand_total, d)}</span>,
      },
      {
        name: "credit_applied", label: "Credit Applied",
        render: (row) => {
          const credit = Number(row.credit_applied) || 0;
          return (
            <span style={{ color: credit > 0 ? "#1A7A4A" : "#9CA3AF" }}>
              {credit > 0 ? fmtCurrency(credit, d) : "—"}
            </span>
          );
        },
      },
      {
        name: "paid", label: "Paid Amount",
        render: (row) => {
          const paid = Number(row.amount_paid) || 0;
          return <span style={{ color: paid > 0 ? "#1A7A4A" : "#9CA3AF" }}>{fmtCurrency(paid, d)}</span>;
        },
      },
      {
        name: "balance", label: "Balance",
        render: (row) => {
          const balance = getInvoiceBalance(row);
          const settled = balance <= 0;
          return settled
            ? <span style={{ color: "#1A7A4A", fontWeight: 600 }}>✓ Settled</span>
            : <span style={{ color: "#DC2626", fontWeight: 600 }}>{fmtCurrency(balance, d)}</span>;
        },
      },
      {
        name: "status", label: "Status",
        render: (row) => {
          const overdue = isOverdue(row);
          if (row.payment_status === "Paid") {
            return <span className="inv-status-badge" style={{ background: "rgba(26,122,74,0.1)", color: "#1A7A4A" }}>Paid</span>;
          }
          if (row.payment_status === "Partial") {
            return <span className="inv-status-badge" style={{ background: "rgba(79,70,229,0.1)", color: "#4F46E5" }}>Partial</span>;
          }
          if (overdue) {
            return <span className="inv-status-badge" style={{ background: "rgba(220,38,38,0.12)", color: "#DC2626" }}>Unpaid</span>;
          }
          return <span className="inv-status-badge" style={{ background: "rgba(245,158,11,0.1)", color: "#D97706" }}>Unpaid</span>;
        },
      },
      {
        name: "actions", label: "Actions",
        render: (row) => (
          <div style={{ display: "flex", alignItems: "center", gap: 8, whiteSpace: "nowrap" }}>
            {row.payment_status !== "Paid" ? (
              <button
                className="inv-inline-pay-btn"
                onClick={(e) => { e.stopPropagation(); this.openPayModal(row); }}
                title="Record Payment"
              >
                <em className="icon ni ni-check" style={{ marginRight: 3 }}></em> Pay
              </button>
            ) : (
              <span style={{ color: "#1A7A4A", fontSize: 12, fontWeight: 600 }}>Paid ✓</span>
            )}
            <span
              className="inv-inline-pdf-btn"
              onClick={(e) => { e.stopPropagation(); this.downloadInvoicePdf(row); }}
              title="Print / PDF"
            >
              PDF
            </span>
            {row.payment_status !== "Paid" && row.payment_status !== "Partial" && (
              <span
                className="inv-inline-view-btn"
                onClick={(e) => { e.stopPropagation(); this.openEditModal(row); }}
                title="Edit Invoice"
                style={{ color: "#1C5BDE" }}
              >
                <em className="icon ni ni-edit"></em>
              </span>
            )}
            <span
              className="inv-inline-view-btn"
              onClick={(e) => { e.stopPropagation(); this.openDetail(row); }}
              title="View Invoice"
            >
              <em className="icon ni ni-eye"></em>
            </span>
            {/* Greyed rather than hidden when not deletable, matching the
                sales-order delete button — the admin can see the control and
                the tooltip says why it is off. */}
            <span
              className="inv-inline-view-btn"
              onClick={(e) => {
                e.stopPropagation();
                if (this.isInvoiceDeletable(row)) this.openDeleteModal(row);
              }}
              title={
                this.isInvoiceDeletable(row)
                  ? "Delete Invoice"
                  : "Invoices with a recorded payment or applied credit cannot be deleted"
              }
              style={{
                color: "#DC2626",
                // No pointerEvents:"none" here — that would also swallow the
                // title tooltip explaining why it is disabled. The onClick
                // guard above is what actually blocks the action.
                cursor: this.isInvoiceDeletable(row) ? "pointer" : "not-allowed",
                opacity: this.isInvoiceDeletable(row) ? 1 : 0.3,
              }}
            >
              <em className="icon ni ni-trash"></em>
            </span>
          </div>
        ),
      },
    ];

    // Fixed column widths, as the SalesCRM grid does, so table-layout: fixed holds.
    // billing_period holds a full "MM-DD-YYYY - MM-DD-YYYY" range on one line.
    const COL_W = { invoice_no: 140, orderno: 110, client: 190, billing_period: 215, status: 100, actions: 190 };
    // Actions stays pinned to the right edge while the grid scrolls sideways.
    const cellClass = (col) => "tb-tnx-info" + (col.name === "actions" ? " inv-col-sticky" : "");

    return (
      <div className="card card-bordered">
        <div className="card-inner border-bottom">{this.renderFilters()}</div>
        {total === 0 && !loading ? (
          <div className="card-inner">
            <div className="inv-empty-state">
              <div className="inv-empty-icon">🧾</div>
              <div className="inv-empty-title">No invoices yet</div>
              <div className="inv-empty-sub">Your invoices and payment records will appear here.</div>
              <button className="btn btn-primary" onClick={this.openCreate}>
                + Create Invoice
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="card-inner p-0" style={{ overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
              <table className="table inv-list-table" style={{ tableLayout: "fixed", marginBottom: 0 }}>
                <colgroup>
                  {columns.map((col) => (
                    <col key={col.name} style={{ width: COL_W[col.name] || 120 }} />
                  ))}
                </colgroup>
                <thead className="tb-tnx-head">
                  <tr className="tb-tnx-item">
                    {columns.map((col) => (
                      <th key={col.name} className={cellClass(col)}>{col.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={columns.length} style={{ textAlign: "center", padding: "32px", color: "#6B6762" }}>
                        <span className="spinner-border spinner-border-sm" />
                        <span style={{ marginLeft: "8px" }}>Loading…</span>
                      </td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={columns.length} style={{ textAlign: "center", padding: "32px", color: "#9A9690" }}>
                        No invoices match your filters.
                      </td>
                    </tr>
                  ) : (
                    rows.map((row, idx) => (
                      <tr key={idx} className="tb-tnx-item" style={{ cursor: "pointer" }} onClick={() => this.openDetail(row)}>
                        {columns.map((col) => (
                          <td key={col.name} className={cellClass(col)}>
                            {typeof col.render === "function"
                              ? col.render(row)
                              : row[col.name] != null ? String(row[col.name]) : ""}
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {this.renderPagination()}
          </>
        )}
      </div>
    );
  }

  // ── Render: create / edit form ────────────────────────────────────────────

  renderForm(mode) {
    const {
      fOrderno, fInvoiceTitle, fCustomerName, fCustomerEmail,
      fInvoiceDate, fDueDate, fPaymentTerms, fCurrency, fInvoiceType, fNotes,
      fPaymentMethod, fPaymentStatus, fAmountPaid, fPaymentDate, fPaymentReference,
      fDiscountTotal, fTaxTotal, fShippingTotal, fLineItems,
      formSubmitting, formError, formSuccess, selectedInvoice,
    } = this.state;

    const isEdit = mode === "edit";
    const { sub, disc, tax, ship, grand } = this.calcTotals();

    return (
      <div>
        {/* Breadcrumb */}
        <div className="inv-breadcrumb">
          <span className="inv-breadcrumb-link" onClick={this.backToList}>Invoices</span>
          <span>›</span>
          <span style={{ color: "#312F2D", fontWeight: 600 }}>
            {isEdit ? `Edit ${selectedInvoice ? selectedInvoice.invoice_no : "Invoice"}` : "New Invoice"}
          </span>
        </div>

        <div id="invoice-print-area" style={{ background: "white", borderRadius: "16px", border: "1px solid #ebe7e1", overflow: "hidden", marginBottom: "14px" }}>
          <div className="inv-header">
            <div>
              <div className="inv-header-brand">VoiceAlerts</div>
              <div className="inv-header-subtitle">DISCONNECTED NUMBER REMEDIATION</div>
            </div>
            <div className="inv-header-right">
              <div className="inv-header-title">INVOICE</div>
              <div className="inv-header-no">{isEdit && selectedInvoice ? selectedInvoice.invoice_no : "DRAFT"}</div>
              <div className={fPaymentStatus === "Paid" ? "inv-status-paid" : "inv-status-pending"}>
                {fPaymentStatus === "Paid" ? "✓ PAID" : "⏳ UNPAID"}
              </div>
            </div>
          </div>
          <div className="inv-body">
            <div className="inv-bill-grid">
              <div>
                <div style={{ fontSize: "10px", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", color: "#9A9690", marginBottom: "10px" }}>Bill To</div>
                <div style={{ fontSize: "15px", fontWeight: 700, color: "#1A1917" }}>{fCustomerName || "Customer"}</div>
                <div style={{ fontSize: "13px", color: "#4D4944", marginTop: "2px" }}>{fCustomerEmail || "—"}</div>
              </div>
              <div>
                <div style={{ fontSize: "10px", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", color: "#9A9690", marginBottom: "10px" }}>Bill From</div>
                <div style={{ fontSize: "15px", fontWeight: 700, color: "#1A1917" }}>VoiceAlerts LLC</div>
                <div style={{ fontSize: "13px", color: "#4D4944", marginTop: "2px" }}>billing@voicealerts.com</div>
              </div>
            </div>
            <div className="inv-order-details">
              {[
                { label: "Order No", value: fOrderno || "—" },
                { label: "Issue Date", value: fmtDate(fInvoiceDate) },
                { label: "Due Date", value: fmtDate(fDueDate) },
                { label: "Payment Terms", value: fPaymentTerms || "Due on receipt" },
              ].map((cell) => (
                <div className="inv-order-cell" key={cell.label}>
                  <div className="inv-order-cell-label">{cell.label}</div>
                  <div className="inv-order-cell-value">{cell.value}</div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="inv-form-wrap">

          {/* Left column */}
          <div className="inv-form-main">

            {/* Header */}
            <div className="inv-form-card">
              <div className="inv-form-section-title">Invoice Details</div>

              <div className="inv-form-row">
                <label className="inv-form-label">Sales Order <span className="inv-req">*</span></label>
                <div style={{ display: "flex", gap: "8px" }}>
                  <input
                    className="inv-form-control"
                    type="text"
                    value={fOrderno}
                    disabled
                    placeholder="Select Sales Order"
                  />
                  <button
                    className="btn btn-secondary"
                    onClick={() => this.openCreate(fOrderno)}
                    disabled={formSubmitting || isEdit}
                    type="button"
                  >
                    Change
                  </button>
                </div>
              </div>

              <div className="inv-form-row">
                <label className="inv-form-label">Invoice Title</label>
                <input
                  className="inv-form-control"
                  type="text"
                  placeholder="e.g. Remediation Services – Q2 2026"
                  value={fInvoiceTitle}
                  onChange={(e) => this.setState({ fInvoiceTitle: e.target.value })}
                  disabled={formSubmitting}
                />
              </div>

              <div className="inv-form-grid-2">
                <div className="inv-form-row">
                  <label className="inv-form-label">Customer Name</label>
                  <input
                    className="inv-form-control"
                    type="text"
                    value={fCustomerName}
                    onChange={(e) => this.setState({ fCustomerName: e.target.value })}
                    disabled={formSubmitting}
                  />
                </div>
                <div className="inv-form-row">
                  <label className="inv-form-label">Customer Email</label>
                  <input
                    className="inv-form-control"
                    type="email"
                    value={fCustomerEmail}
                    onChange={(e) => this.setState({ fCustomerEmail: e.target.value })}
                    disabled={formSubmitting}
                  />
                </div>
              </div>

              <div className="inv-form-grid-2">
                <div className="inv-form-row">
                  <label className="inv-form-label">Invoice Date <span className="inv-req">*</span></label>
                  <input
                    className="inv-form-control"
                    type="date"
                    value={fInvoiceDate}
                    onChange={(e) => this.setState({ fInvoiceDate: e.target.value })}
                    disabled={formSubmitting || isEdit}
                  />
                </div>
                <div className="inv-form-row">
                  <label className="inv-form-label">Due Date <span className="inv-req">*</span></label>
                  <input
                    className="inv-form-control"
                    type="date"
                    value={fDueDate}
                    onChange={(e) => this.setState({ fDueDate: e.target.value })}
                    disabled={formSubmitting}
                  />
                </div>
              </div>

              <div className="inv-form-grid-2">
                <div className="inv-form-row">
                  <label className="inv-form-label">Payment Terms</label>
                  <select
                    className="inv-form-control"
                    value={fPaymentTerms}
                    onChange={(e) => this.setState({ fPaymentTerms: e.target.value })}
                    disabled={formSubmitting}
                  >
                    <option value="Due on receipt">Due on receipt</option>
                    <option value="Net 15">Net 15</option>
                    <option value="Net 30">Net 30</option>
                    <option value="Net 45">Net 45</option>
                    <option value="Net 60">Net 60</option>
                  </select>
                </div>
                <div className="inv-form-row">
                  <label className="inv-form-label">Currency</label>
                  <select
                    className="inv-form-control"
                    value={fCurrency}
                    onChange={(e) => this.setState({ fCurrency: e.target.value })}
                    disabled={formSubmitting}
                  >
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GBP">GBP</option>
                    <option value="CAD">CAD</option>
                  </select>
                </div>
              </div>

              <div className="inv-form-row">
                <label className="inv-form-label">Invoice Type</label>
                <select
                  className="inv-form-control"
                  value={fInvoiceType}
                  onChange={() => {}}
                  disabled
                >
                  <option value="standard">Standard</option>
                </select>
              </div>
            </div>

            {/* Line items */}
            <div className="inv-form-card">
              <div className="inv-form-section-title">Line Items</div>
              <table className="inv-items-table">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th className="right" style={{ width: "80px" }}>QTY</th>
                    <th className="right" style={{ width: "130px" }}>Unit Price</th>
                    <th className="right" style={{ width: "120px" }}>Amount</th>
                    <th style={{ width: "40px" }}></th>
                  </tr>
                </thead>
                <tbody>
                  {fLineItems.map((item, idx) => {
                    const amt = (Number(item.qty) || 0) * (Number(item.unit_price) || 0);
                    return (
                      <tr key={idx}>
                        <td>
                          <input
                            className="inv-inline-input"
                            type="text"
                            placeholder="Description"
                            value={item.description}
                            onChange={(e) => this.updateLineItem(idx, "description", e.target.value)}
                            disabled={formSubmitting}
                          />
                        </td>
                        <td className="right">
                          <input
                            className="inv-inline-input right"
                            type="number"
                            min="1"
                            value={item.qty}
                            onChange={(e) => this.updateLineItem(idx, "qty", e.target.value)}
                            disabled={formSubmitting}
                          />
                        </td>
                        <td className="right">
                          <input
                            className="inv-inline-input right"
                            type="number"
                            min="0"
                            step="0.01"
                            placeholder="0.00"
                            value={item.unit_price}
                            onChange={(e) => this.updateLineItem(idx, "unit_price", e.target.value)}
                            disabled={formSubmitting}
                          />
                        </td>
                        <td className="right" style={{ fontWeight: 700, color: "#1A1917", fontSize: "13px", paddingRight: "12px" }}>
                          {fmtCurrency(amt, this._decimals)}
                        </td>
                        <td style={{ textAlign: "center" }}>
                          <button
                            className="inv-remove-btn"
                            onClick={() => this.removeLineItem(idx)}
                            disabled={formSubmitting || fLineItems.length === 1}
                            title="Remove row"
                          >
                            ×
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <button className="inv-add-item-btn" onClick={this.addLineItem} disabled={formSubmitting}>
                + Add Line Item
              </button>
            </div>

            {/* Notes */}
            <div className="inv-form-card">
              <div className="inv-form-section-title">Notes</div>
              <textarea
                className="inv-form-control"
                rows={4}
                placeholder="Optional notes visible on the invoice…"
                value={fNotes}
                onChange={(e) => this.setState({ fNotes: e.target.value })}
                disabled={formSubmitting}
              />
            </div>

            <div className="inv-form-card">
              <div className="inv-form-section-title">Payment</div>
              <div className="inv-form-grid-2">
                <div className="inv-form-row">
                  <label className="inv-form-label">Payment Method</label>
                  <input
                    className="inv-form-control"
                    type="text"
                    value={fPaymentMethod}
                    onChange={(e) => this.setState({ fPaymentMethod: e.target.value })}
                    disabled={formSubmitting}
                    placeholder="Wire Transfer / Card / ACH"
                  />
                </div>
                <div className="inv-form-row">
                  <label className="inv-form-label">Payment Status</label>
                  <select
                    className="inv-form-control"
                    value={fPaymentStatus}
                    onChange={(e) => this.setState({ fPaymentStatus: e.target.value })}
                    disabled={formSubmitting}
                  >
                    <option value="Pending">Unpaid</option>
                    <option value="Partial">Partial</option>
                    <option value="Paid">Paid</option>
                  </select>
                </div>
              </div>
              <div className="inv-form-grid-2">
                <div className="inv-form-row">
                  <label className="inv-form-label">Amount Paid</label>
                  <input
                    className="inv-form-control"
                    type="number"
                    min="0"
                    step="0.01"
                    value={fAmountPaid}
                    onChange={(e) => this.setState({ fAmountPaid: e.target.value })}
                    disabled={formSubmitting}
                  />
                </div>
                <div className="inv-form-row">
                  <label className="inv-form-label">Payment Date</label>
                  <input
                    className="inv-form-control"
                    type="date"
                    value={fPaymentDate}
                    onChange={(e) => this.setState({ fPaymentDate: e.target.value })}
                    disabled={formSubmitting}
                  />
                </div>
              </div>
              <div className="inv-form-row">
                <label className="inv-form-label">Reference</label>
                <input
                  className="inv-form-control"
                  type="text"
                  value={fPaymentReference}
                  onChange={(e) => this.setState({ fPaymentReference: e.target.value })}
                  disabled={formSubmitting}
                  placeholder="Transaction reference"
                />
              </div>
            </div>

            {/* Feedback */}

            {/* Actions */}
            <div className="inv-form-actions">
              <button className="btn btn-light" onClick={this.backToList} disabled={formSubmitting}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={isEdit ? this.submitEdit : this.submitCreate}
                disabled={formSubmitting}
              >
                {formSubmitting ? "Saving…" : isEdit ? "Save Changes" : "Save Invoice"}
              </button>
            </div>
          </div>

          {/* Right column: totals */}
          <div className="inv-form-sidebar">
            <div className="inv-form-card">
              <div className="inv-form-section-title">Totals</div>

              <div className="inv-totals-row">
                <span>Subtotal</span>
                <span>{fmtCurrency(sub, this._decimals)}</span>
              </div>

              <div className="inv-totals-row" style={{ paddingTop: "10px" }}>
                <label className="inv-form-label" style={{ marginBottom: 0 }}>Discount</label>
                <input
                  className="inv-form-control" type="number" min="0" step="0.01"
                  style={{ width: "100px", textAlign: "right" }}
                  value={fDiscountTotal}
                  onChange={(e) => this.setState({ fDiscountTotal: e.target.value })}
                  disabled={formSubmitting}
                />
              </div>
              <div className="inv-totals-row">
                <span>−{fmtCurrency(disc, this._decimals)}</span>
              </div>

              <div className="inv-totals-row" style={{ paddingTop: "10px" }}>
                <label className="inv-form-label" style={{ marginBottom: 0 }}>Tax</label>
                <input
                  className="inv-form-control" type="number" min="0" step="0.01"
                  style={{ width: "100px", textAlign: "right" }}
                  value={fTaxTotal}
                  onChange={(e) => this.setState({ fTaxTotal: e.target.value })}
                  disabled={formSubmitting}
                />
              </div>
              <div className="inv-totals-row">
                <span>+{fmtCurrency(tax, this._decimals)}</span>
              </div>

              <div className="inv-totals-row" style={{ paddingTop: "10px" }}>
                <label className="inv-form-label" style={{ marginBottom: 0 }}>Shipping</label>
                <input
                  className="inv-form-control" type="number" min="0" step="0.01"
                  style={{ width: "100px", textAlign: "right" }}
                  value={fShippingTotal}
                  onChange={(e) => this.setState({ fShippingTotal: e.target.value })}
                  disabled={formSubmitting}
                />
              </div>
              <div className="inv-totals-row">
                <span>+{fmtCurrency(ship, this._decimals)}</span>
              </div>

              <div className="inv-grand-total" style={{ marginTop: "8px" }}>
                <span>Grand Total</span>
                <span>{fmtCurrency(grand, this._decimals)}</span>
              </div>
            </div>
          </div>

        </div>
      </div>
    );
  }

  // ── Render: detail view ───────────────────────────────────────────────────

  renderDetail() {
    const { detailData, detailLoading, detailError, selectedInvoice } = this.state;

    if (detailLoading) {
      return <div style={{ padding: "40px", textAlign: "center", color: "#6B6762" }}>Loading invoice…</div>;
    }
    if (detailError) {
      return (
        <div>
          <button className="btn btn-secondary btn-sm" onClick={this.backToList}>← Back</button>
        </div>
      );
    }
    if (!detailData && !selectedInvoice) return null;

    const inv = detailData || selectedInvoice;
    const isPaid     = inv.payment_status === "Paid";
    const isPending  = inv.payment_status !== "Paid";
    const isWireACH  = inv.payment_method && (inv.payment_method.toLowerCase().includes("wire") || inv.payment_method.toLowerCase().includes("ach"));
    const isRemediation = inv.invoice_type === "remediation";

    return (
      <div>
        {/* Action bar */}
        <div className="inv-actions">
          <div className="inv-breadcrumb" style={{ marginBottom: 0 }}>
            <span className="inv-breadcrumb-link" onClick={this.backToList}>Invoices</span>
            <span>›</span>
            <span style={{ color: "#312F2D", fontWeight: 600 }}>{inv.invoice_no}</span>
          </div>
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            {isPending && (
              <button className="btn btn-success" onClick={() => this.openPayModal(inv)}>
                <em className="icon ni ni-coin" style={{ fontSize: "13px", marginRight: 4 }}></em>
                Record Payment
              </button>
            )}
            <button className="btn btn-white btn-dim btn-outline-primary" onClick={() => this.openEdit(inv)}>
              <em className="icon ni ni-edit" style={{ fontSize: "13px", marginRight: 4 }}></em>
              Edit
            </button>
            <button className="btn btn-white btn-dim btn-outline-primary" onClick={this.printInvoice}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 4 }}>
                <polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2"/><rect x="6" y="14" width="12" height="8"/>
              </svg>
              Print / PDF
            </button>
            <button className="btn btn-white btn-dim btn-outline-primary" onClick={this.printInvoice} title="Download as PDF via print dialog">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 4 }}>
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Download
            </button>
            {isPaid && isRemediation && (
              <button className="btn btn-success">Submit Numbers →</button>
            )}
          </div>
        </div>

        {/* Invoice card */}
        <div id="invoice-print-area">

          {/* Navy header */}
          <div className="inv-header">
            <div>
              <div className="inv-header-brand">VoiceAlerts</div>
              <div className="inv-header-subtitle">DISCONNECTED NUMBER REMEDIATION</div>
            </div>
            <div className="inv-header-right">
              <div className="inv-header-title">INVOICE</div>
              <div className="inv-header-no">{inv.invoice_no}</div>
              <div className={isPaid ? "inv-status-paid" : "inv-status-pending"}>
                {isPaid ? "✓ PAID" : "⏳ UNPAID"}
              </div>
            </div>
          </div>

          <div className="inv-body">

            {/* Bill To / From */}
            <div className="inv-bill-grid">
              <div>
                <div style={{ fontSize: "10px", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", color: "#9A9690", marginBottom: "10px" }}>Bill To</div>
                <div style={{ fontSize: "15px", fontWeight: 700, color: "#1A1917" }}>{inv.customer_name || "—"}</div>
                {inv.customer_email && <div style={{ fontSize: "13px", color: "#4D4944", marginTop: "2px" }}>{inv.customer_email}</div>}
              </div>
              <div>
                <div style={{ fontSize: "10px", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", color: "#9A9690", marginBottom: "10px" }}>Bill From</div>
                <div style={{ fontSize: "15px", fontWeight: 700, color: "#1A1917" }}>VoiceAlerts LLC</div>
                <div style={{ fontSize: "13px", color: "#4D4944", marginTop: "2px" }}>billing@voicealerts.com</div>
                <div style={{ fontSize: "12px", color: "#6B6762", marginTop: "1px" }}>1 Commerce Plaza, New York, NY 10005</div>
              </div>
            </div>

            {/* Order details strip */}
            <div className="inv-order-details">
              {[
                { label: "Order No",       value: inv.orderno,       mono: true },
                { label: "Issue Date",     value: fmtDate(inv.invoice_date) },
                { label: "Due Date",       value: fmtDate(inv.due_date) },
                { label: "Payment Terms",  value: inv.payment_terms || "Net 30" },
              ].map((cell) => (
                <div className="inv-order-cell" key={cell.label}>
                  <div className="inv-order-cell-label">{cell.label}</div>
                  <div className={`inv-order-cell-value${cell.mono ? " mono" : ""}`}>{cell.value || "—"}</div>
                </div>
              ))}
            </div>

            {/* Line items */}
            <table className="inv-items-table">
              <thead>
                <tr>
                  <th>Description</th>
                  <th className="right">QTY</th>
                  <th className="right">Unit</th>
                  <th className="right">Amount</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <div style={{ fontWeight: 600, color: "#1A1917" }}>{inv.invoice_title || "—"}</div>
                    {inv.notes && <div style={{ fontSize: "12px", color: "#6B6762", marginTop: "2px" }}>{inv.notes}</div>}
                  </td>
                  <td className="right">1</td>
                  <td className="right">{inv.currency || "USD"}</td>
                  <td className="right" style={{ fontWeight: 700, color: "#1A1917" }}>{fmtCurrency(inv.grand_total, this._decimals)}</td>
                </tr>
              </tbody>
            </table>

            {/* Totals */}
            <div className="inv-totals-wrap">
              <div className="inv-totals">
                {[
                  ["Subtotal",     inv.sub_total],
                  ["Est. Discount", inv.discount_total ? `-${fmtCurrency(inv.discount_total, this._decimals)}` : "$0.00"],
                  ["Est. Shipping", fmtCurrency(inv.shipping_total, this._decimals)],
                  ["Est. TAX",      fmtCurrency(inv.tax_total,      this._decimals)],
                ].map(([label, val]) => (
                  <div className="inv-totals-row" key={label}>
                    <span>{label}</span>
                    <span>{typeof val === "string" ? val : fmtCurrency(val, this._decimals)}</span>
                  </div>
                ))}
                <div className="inv-grand-total">
                  <span>Grand Total</span>
                  <span>{fmtCurrency(inv.grand_total, this._decimals)}</span>
                </div>
              </div>
            </div>

            {/* Payment details */}
            <div className="inv-payment-card">
              <div className="inv-payment-label">Payment Details</div>
              <div className="inv-payment-grid">
                <div><span style={{ color: "#6B6762" }}>Payment Method:</span> <strong>{inv.payment_method || "—"}</strong></div>
                <div>
                  <span style={{ color: "#6B6762" }}>Payment Status:</span>{" "}
                  <strong style={{ color: isPaid ? "#1A7A4A" : "#D97706" }}>
                    {paymentStatusLabel(inv.payment_status).toUpperCase()}
                  </strong>
                </div>
              </div>
              {isWireACH && (
                <div className="inv-wire-instructions">
                  <div className="inv-wire-title">Wire / ACH Instructions — VoiceAlerts LLC</div>
                  Bank: <strong>First National Bank of Commerce</strong> · Account: <strong>4892-0017-3341</strong><br />
                  Routing (ACH): <strong>021000021</strong> · SWIFT: <strong>FNBCUS33</strong><br />
                  Reference: <strong style={{ fontFamily: "'Roboto Mono', monospace" }}>{inv.orderno}</strong>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="inv-footer-text">
              Thank you for your business · VoiceAlerts LLC · billing@voicealerts.com · (888) 867-5309<br />
              This invoice was generated automatically. Please retain for your records.
            </div>
          </div>
        </div>

        {/* Bottom buttons */}
        <div className="inv-bottom-btns">
          <button className="btn btn-secondary" onClick={this.backToList}>View All Invoices</button>
          {isPaid && isRemediation && (
            <button className="btn btn-primary" style={{ background: "#0F3A70", borderColor: "#0F3A70" }}>
              Continue — Submit Numbers →
            </button>
          )}
        </div>
      </div>
    );
  }



  // ── Render: client credit ledger modal ───────────────────────────────────

  // React-controlled, not window.$(...).modal() — every modal on this screen is,
  // and mixing Bootstrap's DOM removal with React's reconciler crashes on
  // removeChild (see the note in components/Invoices/CreateInvoiceModal.js).
  renderDeleteModal() {
    const { showDeleteModal, deleteInvoice, deleteReason, deleteError, deleteSubmitting } = this.state;
    if (!showDeleteModal || !deleteInvoice) return null;

    const reasonEmpty = (deleteReason || "").trim() === "";

    return (
      <div
        className="modal fade show"
        style={{ display: "block", background: "rgba(0,0,0,0.45)" }}
        tabIndex="-1"
        role="dialog"
        onClick={(e) => { if (e.target === e.currentTarget && !deleteSubmitting) this.closeDeleteModal(); }}
      >
        <div className="modal-dialog modal-dialog-centered" role="document">
          <div className="modal-content">
            <a href="#cancel" className="close" onClick={(e) => { e.preventDefault(); this.closeDeleteModal(); }}>
              <em className="icon ni ni-cross-sm"></em>
            </a>
            <div className="modal-body modal-body-md">
              <h5 className="title">Delete invoice {deleteInvoice.invoice_no}?</h5>
              <p className="text-soft" style={{ fontSize: "13px" }}>
                The invoice is removed from your invoice list but kept on record.
                A reason is required.
              </p>

              {deleteError !== "" ? (
                <div className="alert alert-pro alert-danger">
                  <div className="alert-text">
                    <h6>Error</h6>
                    <p>{deleteError}</p>
                  </div>
                </div>
              ) : null}

              <div className="form-group">
                <label className="form-label" htmlFor="delete_reason">
                  Reason <sup>*</sup>
                </label>
                <div className="form-control-wrap">
                  <textarea
                    className="form-control"
                    id="delete_reason"
                    name="delete_reason"
                    rows="3"
                    value={deleteReason}
                    onChange={(e) => this.setState({ deleteReason: e.target.value, deleteError: "" })}
                    placeholder="Why is this invoice being deleted?"
                  ></textarea>
                </div>
              </div>

              <div className="form-group text-right">
                <a
                  href="#cancel"
                  className="btn btn-light mr-2"
                  onClick={(e) => { e.preventDefault(); this.closeDeleteModal(); }}
                >
                  Cancel
                </a>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={this.deleteInvoiceSubmit}
                  disabled={reasonEmpty || deleteSubmitting}
                >
                  {deleteSubmitting ? "Deleting..." : "Delete Invoice"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  renderLedgerModal() {
    const { showLedgerModal, ledgerClientName, ledgerClientAccountno, ledgerRows, ledgerLoading, ledgerError } = this.state;
    if (!showLedgerModal) return null;

    const d = this._decimals;
    const formatAmt = (v) => {
      const n = Number(v);
      return Number.isFinite(n) ? n.toFixed(d) : Number(0).toFixed(d);
    };

    const chronoRows = [...ledgerRows].reverse();
    let totalCredits = 0;
    let totalDebits = 0;
    chronoRows.forEach((r) => {
      const amt = Number(r.amount) || 0;
      if (amt > 0) totalCredits += amt;
      else totalDebits += Math.abs(amt);
    });

    let running = 0;
    const displayRows = chronoRows.map((r) => {
      running += Number(r.amount) || 0;
      return { ...r, runningBalance: running };
    });
    const currentBalance = running;

    const tdStyle = { padding: "9px 14px", fontSize: "13px", borderBottom: "1px solid #f5f5f5" };
    const thStyle = {
      padding: "8px 14px", fontSize: "10.5px", fontWeight: 700,
      textTransform: "uppercase", letterSpacing: "0.05em",
      color: "#9a9690", background: "#fafaf9", borderBottom: "1px solid #e5e5e5",
    };

    return (
      <div
        className="modal fade show"
        style={{ display: "block", background: "rgba(0,0,0,0.45)" }}
        tabIndex="-1"
        role="dialog"
        onClick={(e) => { if (e.target === e.currentTarget) this.closeLedgerModal(); }}
      >
        <div className="modal-dialog modal-dialog-centered modal-xl" role="document">
          <div className="modal-content">
            <a href="#cancel" className="close" onClick={(e) => { e.preventDefault(); this.closeLedgerModal(); }}>
              <em className="icon ni ni-cross-sm"></em>
            </a>
            <div className="modal-body" style={{ padding: 0 }}>

              {/* Header */}
              <div style={{ padding: "20px 24px 12px" }}>
                <h5 className="title" style={{ marginBottom: "2px" }}>
                  Credit Ledger{ledgerClientName ? ` — ${ledgerClientName}` : ""}
                </h5>
                <div className="text-soft" style={{ fontSize: "13px" }}>
                  Current Balance:{" "}
                  <strong style={{ color: currentBalance > 0 ? "#1c5bde" : "#9a9690" }}>
                    ${formatAmt(currentBalance)}
                  </strong>
                </div>
              </div>

              {/* Summary strip */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "1px", background: "#e5e5e5", borderTop: "1px solid #e5e5e5", borderBottom: "1px solid #e5e5e5" }}>
                {[
                  ["Total Credits", `+$${formatAmt(totalCredits)}`, "#1a7a4a"],
                  ["Total Debits",  `−$${formatAmt(totalDebits)}`,  "#e85347"],
                  ["Transactions",  String(chronoRows.length),       "#1c5bde"],
                  ["Current Balance", `$${formatAmt(currentBalance)}`, currentBalance > 0 ? "#1c5bde" : "#9a9690"],
                ].map(([lbl, val, col], i) => (
                  <div key={i} style={{ padding: "12px 16px", background: "#fff" }}>
                    <div style={{ fontSize: "10.5px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", color: "#9a9690", marginBottom: "3px" }}>{lbl}</div>
                    <div style={{ fontSize: "18px", fontWeight: 800, color: col, letterSpacing: "-0.01em" }}>{val}</div>
                  </div>
                ))}
              </div>

              {/* Ledger table */}
              <div style={{ overflowX: "auto", overflowY: "auto", maxHeight: "400px" }}>
                {ledgerLoading ? (
                  <div style={{ padding: "32px", textAlign: "center", color: "#9a9690" }}>
                    <span className="spinner-border spinner-border-sm" role="status" />
                    <span style={{ marginLeft: "8px" }}>Loading…</span>
                  </div>
                ) : displayRows.length === 0 ? (
                  <div style={{ padding: "40px", textAlign: "center", color: "#9a9690" }}>
                    <em className="icon ni ni-coin-alt" style={{ fontSize: "32px", display: "block", marginBottom: "10px" }} />
                    <div style={{ fontWeight: 600, color: "#526484" }}>No transactions yet</div>
                    <div style={{ fontSize: "13px", marginTop: "4px" }}>No credit activity for this client.</div>
                  </div>
                ) : (
                  <table style={{ width: "100%", borderCollapse: "collapse", minWidth: "600px" }}>
                    <thead>
                      <tr>
                        <th style={thStyle}>Date</th>
                        <th style={thStyle}>Description</th>
                        <th style={{ ...thStyle, textAlign: "center" }}>Type</th>
                        <th style={{ ...thStyle, textAlign: "right", color: "#1a7a4a" }}>Credit (+)</th>
                        <th style={{ ...thStyle, textAlign: "right", color: "#e85347" }}>Debit (−)</th>
                        <th style={{ ...thStyle, textAlign: "right" }}>Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr style={{ background: "#fafaf9" }}>
                        <td colSpan={5} style={{ ...tdStyle, color: "#9a9690", fontStyle: "italic" }}>Opening Balance</td>
                        <td style={{ ...tdStyle, textAlign: "right", fontWeight: 700, color: "#526484" }}>$0.00</td>
                      </tr>
                      {displayRows.map((row, idx) => {
                        const amt = Number(row.amount) || 0;
                        const isCredit = amt >= 0;
                        return (
                          <tr key={row.id || idx} style={{ background: idx % 2 === 0 ? "#fff" : "#fafaf9" }}>
                            <td style={{ ...tdStyle, color: "#526484", whiteSpace: "nowrap" }}>
                              {this.formatLedgerDate(row.created_at)}
                            </td>
                            <td style={{ ...tdStyle, wordBreak: "break-word", whiteSpace: "normal" }}>
                              {(() => {
                                const dp = (row.description || "").split(" | ").filter(
                                  (p) => !p.startsWith("Date:") && !p.startsWith("[Memo:")
                                );
                                return dp.join(" ") || "—";
                              })()}
                            </td>
                            <td style={{ ...tdStyle, textAlign: "center" }}>
                              <span style={{ display: "inline-block", padding: "2px 8px", borderRadius: "4px", fontSize: "11px", fontWeight: 700, background: isCredit ? "#e6f4ec" : "#fde8e8", color: isCredit ? "#1a7a4a" : "#e85347", textTransform: "uppercase" }}>
                                {isCredit ? "CREDIT" : "DEBIT"}
                              </span>
                            </td>
                            <td style={{ ...tdStyle, textAlign: "right", fontWeight: 700, color: isCredit ? "#1a7a4a" : "#9a9690" }}>
                              {isCredit ? `$${formatAmt(amt)}` : "—"}
                            </td>
                            <td style={{ ...tdStyle, textAlign: "right", fontWeight: 700, color: !isCredit ? "#e85347" : "#9a9690" }}>
                              {!isCredit ? `$${formatAmt(Math.abs(amt))}` : "—"}
                            </td>
                            <td style={{ ...tdStyle, textAlign: "right", fontWeight: 800, color: row.runningBalance > 0 ? "#1c5bde" : "#9a9690" }}>
                              ${formatAmt(row.runningBalance)}
                            </td>
                          </tr>
                        );
                      })}
                      <tr style={{ background: "#1c3558" }}>
                        <td colSpan={5} style={{ ...tdStyle, fontWeight: 700, color: "rgba(255,255,255,0.75)", fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.05em", borderBottom: "none" }}>
                          CLOSING BALANCE
                        </td>
                        <td style={{ ...tdStyle, textAlign: "right", fontWeight: 900, fontSize: "15px", color: "#fff", borderBottom: "none" }}>
                          ${formatAmt(currentBalance)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                )}
              </div>

              {/* Footer */}
              <div style={{ padding: "14px 20px", borderTop: "1px solid #e5e5e5", display: "flex", gap: "10px", alignItems: "center", justifyContent: "flex-end" }}>
                <a href="#close" onClick={(e) => { e.preventDefault(); this.closeLedgerModal(); }} className="link link-light">
                  Close
                </a>
                <button
                  type="button"
                  className="btn btn-dim btn-secondary"
                  onClick={() => {
                    const cols = ["Date", "Description", "Type", "Credit", "Debit", "Balance"];
                    const escCsv = (v) => `"${String(v == null ? "" : v).replace(/"/g, '""')}"`;
                    const rowsCsv = displayRows.map((r) => {
                      const amt = Number(r.amount) || 0;
                      const isCredit = amt >= 0;
                      return [
                        this.formatLedgerDate(r.created_at),
                        r.description || "",
                        isCredit ? "CREDIT" : "DEBIT",
                        isCredit ? formatAmt(amt) : "",
                        !isCredit ? formatAmt(Math.abs(amt)) : "",
                        formatAmt(r.runningBalance),
                      ].map(escCsv).join(",");
                    });
                    const csv = [cols.map(escCsv).join(","), ...rowsCsv].join("\n");
                    const blob = new Blob([csv], { type: "text/csv" });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `ledger-${ledgerClientAccountno || "export"}.csv`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                >
                  <em className="icon ni ni-download"></em>
                  <span>Export CSV</span>
                </button>
              </div>

            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Render: notify modal ──────────────────────────────────────────────────

  renderNotifyModal() {
    const {
      showNotifyModal, notifyFromName, notifyFromEmail,
      notifyToName, notifyTo, notifyCc,
      notifySubject, notifyContent,
      notifyAttachPdf, notifyMarkSent, notifyLogActivity, notifyBccSelf,
      notifySubmitting, notifyError, notifySuccess,
      detailData, selectedInvoice, detailComputed,
    } = this.state;
    if (!showNotifyModal) return null;

    const inv       = detailData || selectedInvoice;
    const grandTotal = Number((inv && inv.grand_total) || 0);
    const balanceDue = inv ? getInvoiceBalance(inv) : 0;
    const d         = this._decimals;
    const isOverdueInv = inv ? isOverdue(inv) : false;
    const defaultContent =
`Dear ${notifyToName || "Client"},\n\nPlease find your invoice for the billing period ${(inv && inv.billing_period) || "—"}.\n\nTotal Amount Due: ${fmtCurrency(grandTotal, d)}\n\nIf you have any questions about this invoice, please don't hesitate to contact us.\n\nBest regards,\n${notifyFromName}${notifyFromEmail ? "\n" + notifyFromEmail : ""}`;

    return (
      <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 1060, display: "flex", alignItems: "flex-start", justifyContent: "center", overflowY: "auto", padding: "24px 16px" }}>
        <div style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 620, boxShadow: "0 20px 60px rgba(0,0,0,0.2)", display: "flex", flexDirection: "column", overflow: "hidden" }}>

          {/* ── Header ── */}
          <div style={{ background: "#0F3A70", padding: "16px 22px", display: "flex", alignItems: "center", gap: 14 }}>
            <div style={{ width: 40, height: 40, borderRadius: 10, background: "rgba(255,255,255,0.12)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <em className="icon ni ni-mail" style={{ color: "#fff", fontSize: 18 }}></em>
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 16, fontWeight: 800, color: "#fff", letterSpacing: -0.3 }}>Send Invoice Email</div>
              <div style={{ fontSize: 12, color: "rgba(255,255,255,0.65)", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {inv && inv.invoice_no} · {(inv && inv.customer_name) || ""} · {fmtCurrency(balanceDue, d)} due
              </div>
            </div>
            <button onClick={this.closeNotifyModal} disabled={notifySubmitting}
              style={{ width: 30, height: 30, borderRadius: 7, border: "1.5px solid rgba(255,255,255,0.3)", background: "transparent", color: "#fff", fontSize: 18, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>×</button>
          </div>

          {/* ── Scrollable body ── */}
          <div ref={this._notifyBodyRef} style={{ padding: "20px 24px", overflowY: "auto", maxHeight: "72vh", display: "flex", flexDirection: "column", gap: 16 }}>

            {/* Invoice summary strip */}
            {inv && (
              <div style={{ background: "#FFFBEB", border: "1.5px solid #FDE68A", borderRadius: 10, padding: "14px 18px", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#D97706", marginBottom: 4 }}>
                    {isOverdueInv ? "⚠ Invoice Overdue" : "Invoice Awaiting Payment"}
                  </div>
                  <div style={{ fontSize: 12, color: "#92400E" }}>
                    {inv.billing_period ? `Billing Period: ${inv.billing_period} · ` : ""}Due: {fmtDate(inv.due_date)}
                  </div>
                </div>
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: "#1A1917" }}>{fmtCurrency(grandTotal, d)}</div>
                  <div style={{ fontSize: 12, color: "#6B6762", marginTop: 2 }}>Balance: {fmtCurrency(balanceDue, d)}</div>
                </div>
              </div>
            )}

            {/* From */}
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: "#4D4944", display: "block", marginBottom: 8 }}>From</label>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <input className="pay-field-input" type="text" placeholder="Sender name"
                  value={notifyFromName}
                  onChange={(e) => this.setState({ notifyFromName: e.target.value })}
                  disabled={notifySubmitting} />
                <input className="pay-field-input" type="email" placeholder="sender@company.com"
                  value={notifyFromEmail}
                  onChange={(e) => this.setState({ notifyFromEmail: e.target.value })}
                  disabled={notifySubmitting} />
              </div>
            </div>

            {/* To */}
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: "#4D4944" }}>To <span style={{ fontWeight: 400, color: "#9A9690" }}>(primary recipient)</span></label>
                <span style={{ fontSize: 11, color: "#9A9690" }}>Auto-filled from client profile</span>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <input className="pay-field-input" type="text" placeholder="Recipient name"
                  value={notifyToName}
                  onChange={(e) => this.setState({ notifyToName: e.target.value })}
                  disabled={notifySubmitting} />
                <input className="pay-field-input" type="email" placeholder="client@company.com"
                  value={notifyTo}
                  onChange={(e) => this.setState({ notifyTo: e.target.value })}
                  disabled={notifySubmitting} />
              </div>
            </div>

            {/* CC */}
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: "#4D4944" }}>CC <span style={{ fontWeight: 400, color: "#9A9690" }}>(optional)</span></label>
                {!notifyCc && (
                  <span onClick={() => this.setState({ notifyCc: " " })}
                    style={{ fontSize: 12, fontWeight: 600, color: "#6576ff", cursor: "pointer" }}>+ Add CC</span>
                )}
              </div>
              {notifyCc !== "" && (
                <div style={{ position: "relative" }}>
                  <input className="pay-field-input" type="text" placeholder="cc@example.com"
                    value={notifyCc.trim()}
                    onChange={(e) => this.setState({ notifyCc: e.target.value })}
                    disabled={notifySubmitting}
                    style={{ paddingRight: 38 }} />
                  <button onClick={() => this.setState({ notifyCc: "" })} disabled={notifySubmitting}
                    style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", width: 24, height: 24, borderRadius: 4, border: "none", background: "#FEE2E2", color: "#DC2626", cursor: "pointer", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
                </div>
              )}
            </div>

            {/* Subject */}
            <div>
              <label style={{ fontSize: 12, fontWeight: 700, color: "#4D4944", display: "block", marginBottom: 8 }}>Subject</label>
              <input className="pay-field-input" type="text"
                value={notifySubject}
                onChange={(e) => this.setState({ notifySubject: e.target.value })}
                disabled={notifySubmitting} />
            </div>

            {/* Message Body */}
            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8 }}>
                <label style={{ fontSize: 12, fontWeight: 700, color: "#4D4944" }}>Message Body</label>
                <span onClick={() => this.setState({ notifyContent: defaultContent })}
                  style={{ fontSize: 12, fontWeight: 600, color: "#6576ff", cursor: "pointer" }}>Reset to default</span>
              </div>
              <textarea className="pay-field-input" rows={8}
                style={{ resize: "vertical", minHeight: 160, fontFamily: "monospace", fontSize: 13, lineHeight: 1.6 }}
                value={notifyContent}
                onChange={(e) => this.setState({ notifyContent: e.target.value })}
                disabled={notifySubmitting} />
            </div>

            {/* PDF attachment */}
            {inv && inv.pdf_path && (
              <div style={{ background: "#EEF2FF", border: "1.5px solid #C7D2FE", borderRadius: 10, padding: "12px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <em className="icon ni ni-clip" style={{ color: "#6576ff", fontSize: 18 }}></em>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#3730A3" }}>Invoice PDF will be attached automatically</div>
                    <div style={{ fontSize: 12, color: "#6575D4", marginTop: 2 }}>
                      {inv.invoice_no} · {fmtCurrency(grandTotal, d)} · {inv.invoice_type === "partial" ? "Partial Invoice" : "Full Invoice"}
                    </div>
                  </div>
                </div>
                <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 13, fontWeight: 600, color: "#3730A3", flexShrink: 0 }}>
                  <input type="checkbox" checked={notifyAttachPdf}
                    onChange={(e) => this.setState({ notifyAttachPdf: e.target.checked })}
                    style={{ accentColor: "#6576ff", width: 15, height: 15 }} />
                  Attach PDF
                </label>
              </div>
            )}

            {/* Send options */}
            <div style={{ background: "#FAFAF9", border: "1px solid #E8E4DF", borderRadius: 10, padding: "14px 16px" }}>
              <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.09em", color: "#9A9690", marginBottom: 12 }}>Send Options</div>
              {[
                { key: "notifyMarkSent",    label: 'Mark invoice as "Sent" after sending' },
                { key: "notifyLogActivity", label: "Log this email in activity history" },
                { key: "notifyBccSelf",     label: `BCC myself (${notifyFromEmail || "your email"})` },
              ].map(({ key, label }) => (
                <label key={key} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 13, color: "#312F2D", marginBottom: 8 }}>
                  <input type="checkbox" checked={this.state[key]}
                    onChange={(e) => this.setState({ [key]: e.target.checked })}
                    style={{ accentColor: "#6576ff", width: 15, height: 15 }} />
                  {label}
                </label>
              ))}
            </div>

          </div>

          {/* ── Footer ── */}
          <div style={{ padding: "12px 24px 16px", borderTop: "1px solid #F0ECE8" }}>
            <div style={{ fontSize: 12, color: "#6B6762", marginBottom: 12 }}>
              Sending to: <strong>{notifyTo || "—"}</strong>
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button className="btn btn-light" onClick={this.closeNotifyModal} disabled={notifySubmitting}>Cancel</button>
              {/* <button className="btn btn-secondary" style={{ display: "flex", alignItems: "center", gap: 6 }}
                onClick={() => window.alert("Preview not implemented yet.")} disabled={notifySubmitting}>
                <em className="icon ni ni-eye" style={{ fontSize: 15 }}></em> Preview
              </button> */}
              <button className="btn btn-primary" onClick={this.submitNotify} disabled={notifySubmitting || !!notifySuccess}
                style={{ display: "flex", alignItems: "center", gap: 6, opacity: notifySuccess ? 0.5 : 1 }}>
                {notifySubmitting
                  ? <><span className="spinner-border spinner-border-sm" style={{ marginRight: 6, width: 14, height: 14, borderWidth: 2 }}></span>Sending…</>
                  : <><em className="icon ni ni-send" style={{ fontSize: 15 }}></em> Send Email</>}
              </button>
            </div>
          </div>

        </div>
      </div>
    );
  }

  // ── Render: generate-from-order modal (preserved) ─────────────────────────

  renderGenerateModal() {
    const {
      showCreateFromOrderModal,
      salesOrdersForInvoice,
      salesOrdersLoading,
      selectedOrderForCreate,
      orderSearchTerm,
      orderSearchOpen,
      existingOrderInvoices,
      existingOrderInvoicesLoading,
    } = this.state;
    if (!showCreateFromOrderModal) return null;

    const lc = (orderSearchTerm || "").toLowerCase();
    const filteredOrders = lc
      ? salesOrdersForInvoice.filter(
          (o) =>
            (o.orderno || "").toLowerCase().includes(lc) ||
            (o.quote_title || "").toLowerCase().includes(lc) ||
            (o.customer_name || o.contact_person || "").toLowerCase().includes(lc)
        )
      : salesOrdersForInvoice;

    const selectedOrder = salesOrdersForInvoice.find((o) => o.orderno === selectedOrderForCreate);

    return (
      <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.5)" }} tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "680px" }}>
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Create Invoice from Sales Order</h5>
              <button type="button" className="close" onClick={this.closeGenerateModal}>
                <em className="icon ni ni-cross"></em>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Sales Order <span style={{ color: "red" }}>*</span></label>
                {salesOrdersLoading ? (
                  <p style={{ color: "#9a9690", fontSize: "13px" }}>Loading sales orders…</p>
                ) : salesOrdersForInvoice.length === 0 ? (
                  <p style={{ color: "#9a9690", fontSize: "13px" }}>No completed sales orders found.</p>
                ) : (
                  <div style={{ position: "relative" }}>
                    {/* Search input */}
                    <div className="input-group" style={{ marginBottom: 0 }}>
                      <div className="input-group-prepend">
                        <span className="input-group-text" style={{ fontSize: 13 }}>
                          <em className="icon ni ni-search"></em>
                        </span>
                      </div>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Search by order no, title, or customer…"
                        value={orderSearchTerm}
                        onChange={(e) => this.setState({ orderSearchTerm: e.target.value, orderSearchOpen: true })}
                        onFocus={() => this.setState({ orderSearchOpen: true })}
                        style={{ fontSize: 13 }}
                        autoComplete="off"
                      />
                      {selectedOrder && !orderSearchOpen && (
                        <div className="input-group-append">
                          <span className="input-group-text" style={{ fontSize: 13, color: "#1A7A4A", fontWeight: 600 }}>
                            ✓ {selectedOrder.orderno}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Dropdown list */}
                    {orderSearchOpen && (
                      <div style={{
                        position: "absolute", top: "100%", left: 0, right: 0, zIndex: 1050,
                        background: "#fff", border: "1px solid #dbdfea", borderRadius: "0 0 6px 6px",
                        boxShadow: "0 4px 16px rgba(0,0,0,0.1)", maxHeight: 240, overflowY: "auto",
                      }}>
                        {filteredOrders.length === 0 ? (
                          <div style={{ padding: "10px 14px", fontSize: 13, color: "#9CA3AF" }}>No orders match your search.</div>
                        ) : filteredOrders.map((o) => (
                          <div
                            key={o.orderno}
                            onClick={() => {
                              this.onSelectOrderForCreate(o.orderno);
                              this.setState({ orderSearchTerm: "", orderSearchOpen: false });
                            }}
                            style={{
                              padding: "10px 14px", cursor: "pointer", borderBottom: "1px solid #f3f3f3",
                              background: selectedOrderForCreate === o.orderno ? "#EEF2FF" : "#fff",
                              display: "flex", justifyContent: "space-between", alignItems: "center",
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = "#F9FAFB"}
                            onMouseLeave={(e) => e.currentTarget.style.background = selectedOrderForCreate === o.orderno ? "#EEF2FF" : "#fff"}
                          >
                            <div>
                              <div style={{ fontWeight: 600, fontSize: 13 }}>{o.orderno}</div>
                              <div style={{ fontSize: 12, color: "#6B7280" }}>
                                {o.quote_title || "Untitled"}
                                {` · ${o.client_name || "NA"}`}
                              </div>
                            </div>
                            <div style={{ textAlign: "right", fontSize: 12 }}>
                              <div style={{ fontWeight: 600, color: "#1F2937" }}>${Number(o.quote_total || 0).toFixed(this._decimals)}</div>
                              {Number(o.invoice_count || 0) > 0 && (
                                <div style={{ color: "#D97706" }}>{o.invoice_count} invoice(s)</div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Overlay to close dropdown on outside click */}
                    {orderSearchOpen && (
                      <div
                        style={{ position: "fixed", inset: 0, zIndex: 1049 }}
                        onClick={() => this.setState({ orderSearchOpen: false })}
                      />
                    )}

                    {/* Selected order chip */}
                    {selectedOrder && (
                      <div style={{ marginTop: 10, padding: "10px 14px", background: "#EEF2FF", borderRadius: 6, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: 13, color: "#1C5BDE" }}>{selectedOrder.orderno}</div>
                          <div style={{ fontSize: 12, color: "#4B5563" }}>
                            {selectedOrder.quote_title || "Untitled"}
                            {` · ${selectedOrder.client_name || "NA"}`}
                            {" · "}${Number(selectedOrder.quote_total || 0).toFixed(this._decimals)}
                          </div>
                        </div>
                        <button
                          className="btn btn-sm btn-outline-secondary"
                          onClick={() => this.setState({ selectedOrderForCreate: "", existingOrderInvoices: [], orderSearchTerm: "" })}
                          style={{ fontSize: 12 }}
                        >
                          Change
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {selectedOrderForCreate && (
                <div className="form-group" style={{ marginTop: "12px" }}>
                  <label className="form-label">Existing Invoices for this Order</label>
                  {existingOrderInvoicesLoading ? (
                    <p style={{ color: "#9a9690", fontSize: "13px" }}>Checking existing invoices…</p>
                  ) : existingOrderInvoices.length === 0 ? (
                    <p style={{ color: "#9a9690", fontSize: "13px", marginBottom: 0 }}>No existing invoice found. You can create a new one.</p>
                  ) : (
                    <div style={{ maxHeight: "180px", overflowY: "auto", border: "1px solid #eee", borderRadius: "8px" }}>
                      {existingOrderInvoices.map((inv) => (
                        <div
                          key={inv.invoice_no}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "10px 12px",
                            borderBottom: "1px solid #f3f3f3",
                          }}
                        >
                          <div>
                            <div style={{ fontWeight: 600 }}>{inv.invoice_no}</div>
                            <div style={{ fontSize: "12px", color: "#7a7772" }}>
                              {fmtDate(inv.invoice_date)} · {fmtCurrency(inv.grand_total, this._decimals)} · {paymentStatusLabel(inv.payment_status)}
                            </div>
                          </div>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => {
                              this.setState({ showCreateFromOrderModal: false });
                              this.openDetail(inv);
                            }}
                          >
                            Open
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary btn-sm" onClick={this.closeGenerateModal}>Cancel</button>
              <button
                className="btn btn-primary btn-sm"
                onClick={this.startCreateFromSelectedOrder}
                disabled={salesOrdersLoading || !selectedOrderForCreate}
              >
                <em className="icon ni ni-plus mr-1"></em>Create Invoice
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Main render ───────────────────────────────────────────────────────────

  render() {
    const { activeView } = this.state;
    return (
      <div className="nk-content">
        <div className="container-fluid">
          {this.props.defaultSettings === "missing" ? <SettingsError /> : null}
          {this.props.is_msa_signed === "No" ? <MsaWarning /> : null}
          <div className="nk-content-inner">
            {/* rem-invoice-wrap stays on an ancestor: it scopes the .inv-* rules and CSS variables */}
            <div className="nk-content-body rem-invoice-wrap">
              {activeView === "list" && (
                <>
                  {this.renderPageHeader()}
                  {this.renderKpiCards()}
                  {this.renderList()}
                </>
              )}
              {activeView !== "list" && (
                <div className="card card-bordered">
                  <div className="card-inner">
                    {activeView === "create" && this.renderForm("create")}
                    {activeView === "edit"   && this.renderForm("edit")}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        <ViewInvoiceModal
          show={this.state.showViewModal}
          invoiceNo={this.state.selectedInvoice && this.state.selectedInvoice.invoice_no}
          onClose={this.closeViewModal}
          onPaymentRecorded={this.handlePaymentSuccess}
          onFollowUp={this.openFollowUpCreate}
          onNotify={this.openNotifyModal}
        />
        {this.renderNotifyModal()}
        {this.renderLedgerModal()}
        {this.renderGenerateModal()}
        {this.renderDeleteModal()}
        <EditInvoiceModal
          show={this.state.showEditModal}
          invoice={this.state.editInvoice}
          onClose={this.closeEditModal}
          onSuccess={this.handleEditSuccess}
        />
        <RecordPaymentModal
          show={this.state.showPayModal}
          invoice={this.state.payInvoice}
          decimals={this._decimals}
          onClose={this.closePayModal}
          onSuccess={this.handlePaymentSuccess}
        />
        <CreateInvoiceModal
          show={this.state.showFollowUpModal}
          mode={this.state.followUpMode}
          salesOrder={this.state.followUpSalesOrder}
          onClose={this.close_follow_up_modal}
          onSuccess={this.handleFollowUpSuccess}
        />
        <NotificationToast
          message={this.state.toastMsg}
          type={this.state.toastType}
          onDismiss={() => this.setState({ toastMsg: "" })}
        />
      </div>
    );
  }
}

const mapStateToProps = (state) => ({ defaultSettings: state.settings });
const mapDispatchToProps = (dispatch) => ({
  addSettings: (data) => dispatch(addSettings(data)),
  addMsaInfo:  (data) => dispatch(addMsaInfo(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(Invoices));
